export type Stats = {
  stamina: number;
  intellect: number;
  charm: number;
  empathy: number;
  luck: number;
  health: number;
  money: number;
};

export type Gender = "female" | "male";

export type LifePhase =
  | "child"
  | "teen"
  | "young"
  | "adult"
  | "middle"
  | "elder";

export type GameEvent = {
  id: string;
  title: string;
  text: string;
  phase?: LifePhase[];
  minAge?: number;
  maxAge?: number;
  weight: number;
  effects: Partial<Stats>;
  tags: string[];
  genders?: Gender[];
  requiresChildren?: boolean;
  requiresPartner?: boolean;
  targetRelation?: "parent" | "partner" | "descendant";
  targetMinAge?: number;
  targetMaxAge?: number;
};

export type ChildRandomEvent = {
  id: string;
  title: string;
  text: string;
  minAge: number;
  maxAge: number;
  weight: number;
  effects: Partial<Stats>;
};

export type Choice = {
  id: string;
  label: string;
  text: string;
  effects: Partial<Stats>;
  requires?: Partial<Stats>;
  next: string;
};

export type RelationshipStage = "single" | "dating" | "engaged" | "married";

export type RelationshipAction =
  | "start-dating"
  | "stay-single"
  | "grow"
  | "propose"
  | "marry"
  | "adopt"
  | "postpone"
  | "break-up";

export type RelationshipChoice = Choice & {
  action: RelationshipAction;
  affection: number;
};

export type RelationshipScene = {
  id: string;
  kind: "teen-encounter" | "teen-dating" | "encounter" | "reunion" | "dating" | "proposal" | "engagement" | "married" | "adoption";
  title: string;
  text: string;
  choices: RelationshipChoice[];
};

export type Milestone = {
  id: string;
  age: number;
  title: string;
  text: string;
  choices: Choice[];
};

export type GenerationArc = {
  id: number;
  familyName: string;
  title: string;
  prologue: string;
  legacy: string;
  heirloom: string;
  bonus: Partial<Stats>;
  ambition: string;
};

export const BASE_STATS: Stats = {
  stamina: 52,
  intellect: 52,
  charm: 48,
  empathy: 50,
  luck: 46,
  health: 78,
  money: 120,
};

export const GENERATION_ARCS: GenerationArc[] = [
  {
    id: 1,
    familyName: "青柳",
    title: "便利商店燈下的第一本帳",
    prologue:
      "你出生在通勤線旁的小公寓。家人不富裕，卻把每一張發票都收進餅乾鐵盒，說生活的魔法藏在細節裡。",
    legacy:
      "第一代學會了把小錢變成選擇，把失敗寫成下一代看得懂的提醒。",
    heirloom: "一本貼滿收據的家計簿",
    bonus: { intellect: 3, empathy: 2, money: 60 },
    ambition: "建立一個不再被月底追著跑的家庭。",
  },
  {
    id: 2,
    familyName: "青柳",
    title: "雨季裡的補習班白板",
    prologue:
      "你在父母租下的小書桌旁長大。窗外總在下雨，白板上卻寫著晴天才敢想的志願。",
    legacy:
      "第二代留下教育資源和穩定讀書習慣，家族開始相信知識能改變命運。",
    heirloom: "一支磨到短短的白板筆",
    bonus: { intellect: 6, stamina: 1, money: 90 },
    ambition: "用學歷與專業換取更寬的城市地圖。",
  },
  {
    id: 3,
    familyName: "青柳",
    title: "新創辦公室的深夜咖啡",
    prologue:
      "你出生時，家裡已有一台二手筆電和一張投資清單。大人們談論股票、產品、加班與夢想。",
    legacy:
      "第三代把冒險精神帶進家族，財富可能快速上升，也可能在一次錯判中蒸發。",
    heirloom: "貼著便利貼的舊筆電",
    bonus: { intellect: 4, luck: 4, money: 160 },
    ambition: "做出一件真正被世界需要的事。",
  },
  {
    id: 4,
    familyName: "青柳",
    title: "老街診所的午後藥袋",
    prologue:
      "家族搬到有樟樹的老街。你常在診所門口看見不同人生，有人康復，有人告別。",
    legacy:
      "第四代理解健康與照護的重量，家族不只追求收入，也開始累積人脈與善意。",
    heirloom: "一只刻著名字的聽診器盒",
    bonus: { empathy: 6, health: 6, money: 120 },
    ambition: "讓照顧別人成為一種可以長久維持的能力。",
  },
  {
    id: 5,
    familyName: "青柳",
    title: "海邊民宿與褪色明信片",
    prologue:
      "你在靠海的小鎮出生，家裡經營週末民宿。每位旅人都留下故事，也留下淡淡的鹽味。",
    legacy:
      "第五代把魅力、服務與地方感帶進家族，資產開始從現金變成房產與品牌。",
    heirloom: "一疊旅人寄回的明信片",
    bonus: { charm: 6, empathy: 3, money: 220 },
    ambition: "把家變成別人願意回來的地方。",
  },
  {
    id: 6,
    familyName: "青柳",
    title: "海外車站的冬日行李箱",
    prologue:
      "你出生後不久，全家因工作短暫移居海外。陌生語言像雪落下，慢慢積成新的路。",
    legacy:
      "第六代留下國際視野與語言優勢，也讓家族第一次認真思考身份與歸屬。",
    heirloom: "一只貼滿轉機標籤的行李箱",
    bonus: { intellect: 3, charm: 3, luck: 3, money: 260 },
    ambition: "在不同文化之間找到自己的座標。",
  },
  {
    id: 7,
    familyName: "青柳",
    title: "安養院庭院的柚子樹",
    prologue:
      "家族人口變多，也變老。你在長輩的故事中長大，知道每個選擇都會在很久之後回聲。",
    legacy:
      "第七代學會長照、保險與家庭協商，財富不再只是數字，也是照顧彼此的餘裕。",
    heirloom: "一張泛黃的家族合照",
    bonus: { empathy: 5, health: 4, money: 300 },
    ambition: "讓家族在老去時仍保有尊嚴。",
  },
  {
    id: 8,
    familyName: "青柳",
    title: "城市屋頂上的小型菜園",
    prologue:
      "你出生在高樓之間。家人在屋頂種菜、裝太陽能板，談的是氣候，也談未來。",
    legacy:
      "第八代把永續投資和生活韌性放進家訓，家族財富開始連結社會影響。",
    heirloom: "一包標著年份的番茄種子",
    bonus: { stamina: 3, empathy: 3, luck: 2, money: 340 },
    ambition: "在動盪世界裡活得穩，也活得有意義。",
  },
  {
    id: 9,
    familyName: "青柳",
    title: "AI 實驗室的玻璃白噪音",
    prologue:
      "你出生時，家族已習慣用工具放大能力。螢幕裡的模型回答問題，餐桌上的人回答彼此。",
    legacy:
      "第九代擁抱自動化與創意工作，家族站到新技術浪潮前，也面臨更複雜的倫理選擇。",
    heirloom: "一枚刻著初始化日期的晶片吊飾",
    bonus: { intellect: 7, luck: 2, money: 420 },
    ambition: "讓技術服務人，而不是吞掉人的生活。",
  },
  {
    id: 10,
    familyName: "青柳",
    title: "百年家宴的紙燈籠",
    prologue:
      "第十代出生在一場家宴後。牆上掛著前九代留下的照片，每張臉都像在問：你要把故事帶去哪裡？",
    legacy:
      "第十代繼承完整家族記憶，能選擇守成、革新，或把資產用於更大的公共夢想。",
    heirloom: "寫滿十代名字的紙燈籠",
    bonus: { stamina: 2, intellect: 4, charm: 4, empathy: 4, luck: 4, money: 520 },
    ambition: "決定家族接下來一百年的方向。",
  },
];

export const MILESTONES: Milestone[] = [
  {
    id: "age-3",
    age: 3,
    title: "第一次選擇幼兒園",
    text: "你開始需要更穩定的團體生活。家人把距離、費用、照顧時間與你的個性放在一起考量。",
    choices: [
      {
        id: "kindergarten-public",
        label: "社區公立幼兒園",
        text: "作息規律、費用較低，也能和附近的孩子一起長大。",
        effects: { empathy: 5, stamina: 2, money: -15 },
        next: "你建立團體生活的安全感，家庭保留較多資產。",
      },
      {
        id: "kindergarten-private",
        label: "私立雙語幼兒園",
        text: "語言和活動資源更豐富，學費與每天的行程也更高。",
        effects: { intellect: 7, charm: 4, health: -1, money: -90 },
        requires: { money: 90 },
        next: "表達能力提早成長，家庭開始承擔較高教育支出。",
      },
      {
        id: "kindergarten-forest",
        label: "自然探索幼兒園",
        text: "大量戶外活動讓你從泥土、植物和合作遊戲中學習。",
        effects: { stamina: 7, health: 4, intellect: 2, money: -65 },
        requires: { money: 65 },
        next: "身體與好奇心一起長大，接送與裝備也更費心。",
      },
      {
        id: "kindergarten-home",
        label: "家庭共育與公共托育",
        text: "使用公共資源並由家人輪流照顧，省下費用但占用更多時間。",
        effects: { empathy: 7, intellect: 2, stamina: -1, money: -10 },
        next: "親子關係更緊密，照顧者的時間壓力也增加。",
      },
    ],
  },
  {
    id: "age-6",
    age: 6,
    title: "小學入學",
    text: "家人把學費、距離與你的個性攤在桌上討論。第一所學校會成為你長時間生活的環境。",
    choices: [
      {
        id: "elementary-public",
        label: "就讀社區公立小學",
        text: "學費較低、離家較近，你和附近的孩子一起長大。",
        effects: { intellect: 4, empathy: 5, money: -20 },
        next: "家庭保留更多資產，你也建立穩定的生活圈。",
      },
      {
        id: "elementary-private",
        label: "進入私立菁英小學",
        text: "小班教學、密集課程與更高期待同時來到。",
        effects: { intellect: 10, charm: 3, health: -2, money: -130 },
        requires: { money: 130 },
        next: "學習進度超前，家庭資產與你的壓力都受到影響。",
      },
      {
        id: "elementary-bilingual",
        label: "選擇雙語國際小學",
        text: "部分課程改用外語，活動與同學背景也更多元。",
        effects: { intellect: 7, charm: 7, luck: 2, money: -180 },
        requires: { money: 180 },
        next: "語言和適應力提升，教育支出也大幅增加。",
      },
      {
        id: "elementary-experimental",
        label: "嘗試實驗教育",
        text: "課程圍繞專題與探索，家人也必須投入更多陪伴。",
        effects: { intellect: 6, empathy: 5, luck: 4, money: -90 },
        requires: { money: 90 },
        next: "你保留強烈的好奇心，也走上一條較不標準的路。",
      },
    ],
  },
  {
    id: "age-9",
    age: 9,
    title: "課後時間怎麼使用",
    text: "放學後不再只是空白。你開始表達喜歡什麼，家人也要衡量時間與費用。",
    choices: [
      {
        id: "interest-reading",
        label: "閱讀與科學探索",
        text: "你把問題帶進圖書館和科學教室，一路追到答案。",
        effects: { intellect: 8, empathy: 1, money: -25 },
        next: "理解力與專注力持續成長。",
      },
      {
        id: "interest-sports",
        label: "加入運動校隊",
        text: "訓練和比賽成為生活的一部分。",
        effects: { stamina: 8, health: 5, money: -30 },
        next: "身體底子變好，也學會面對輸贏。",
      },
      {
        id: "interest-arts",
        label: "學音樂或美術",
        text: "練習、作品和器材慢慢填滿房間。",
        effects: { charm: 8, empathy: 3, money: -55 },
        requires: { money: 55 },
        next: "你找到表達自己的方式，也增加了固定支出。",
      },
      {
        id: "interest-free",
        label: "保留自由探索",
        text: "家人不排滿課表，讓你自己安排遊戲、朋友與休息。",
        effects: { luck: 5, empathy: 4, health: 2 },
        next: "自主性增加，家庭也沒有額外負擔。",
      },
    ],
  },
  {
    id: "age-12",
    age: 12,
    title: "國中升學",
    text: "你已經能清楚表達意見。家人提出預算與期待，而你開始決定想在什麼環境度過青春期。",
    choices: [
      {
        id: "junior-public",
        label: "就讀學區公立國中",
        text: "維持熟悉的生活圈，把預算留給課外探索。",
        effects: { intellect: 5, empathy: 4, money: -25 },
        next: "生活相對穩定，你有空間自己安排方向。",
      },
      {
        id: "junior-private",
        label: "進入私立升學國中",
        text: "密集考試、晚自習與升學輔導讓路線提早成形。",
        effects: { intellect: 12, health: -4, money: -160 },
        requires: { money: 160 },
        next: "成績快速上升，壓力與支出也跟著增加。",
      },
      {
        id: "junior-bilingual",
        label: "選擇雙語特色國中",
        text: "你用另一種語言上課，也參加更多跨校活動。",
        effects: { intellect: 8, charm: 7, luck: 2, money: -190 },
        requires: { money: 190 },
        next: "語言與社交視野擴大，家庭支出明顯提高。",
      },
      {
        id: "junior-vocational",
        label: "參加技藝探索課程",
        text: "除了課本，你也接觸設計、機械與生活技術。",
        effects: { intellect: 6, stamina: 5, charm: 2, money: -65 },
        next: "你更早看見考試之外的能力。",
      },
    ],
  },
  {
    id: "age-15",
    age: 15,
    title: "高中路線選擇",
    text: "這次不只是學校名稱，而是普通升學、技術專業或專長訓練的分岔。你的意見開始比家人的安排更有重量。",
    choices: [
      {
        id: "senior-public",
        label: "公立普通高中",
        text: "兼顧升學與社團，費用相對可控。",
        effects: { intellect: 9, empathy: 2, money: -45 },
        next: "你保留多種升學可能，家庭仍有餘裕。",
      },
      {
        id: "senior-elite",
        label: "私立菁英高中",
        text: "升學資源與競爭都非常密集。",
        effects: { intellect: 15, charm: 3, health: -5, money: -210 },
        requires: { money: 210 },
        next: "學歷優勢變大，壓力與費用也很可觀。",
      },
      {
        id: "senior-vocational",
        label: "技術型高中",
        text: "你選定專業群科，提早累積能換成工作的技能。",
        effects: { intellect: 8, stamina: 7, luck: 2, money: -70 },
        next: "你可以直接就業，也保留繼續升學的路。",
      },
      {
        id: "senior-specialty",
        label: "藝術或體育專校",
        text: "把大量時間投入專長，成績不再是唯一標準。",
        effects: { charm: 10, stamina: 5, luck: 4, money: -140 },
        requires: { money: 140 },
        next: "專長快速成形，未來路線也更加集中。",
      },
    ],
  },
  {
    id: "age-18",
    age: 18,
    title: "成年門口",
    text: "你可以離家、留下、借錢、冒險。每個選項都像簽下一張看不見的契約。",
    choices: [
      {
        id: "university",
        label: "讀大學累積專業",
        text: "你用學費換時間，也用時間換更高的天花板。",
        effects: { intellect: 14, money: -120, charm: 2 },
        next: "學歷提高，短期資產下降。",
      },
      {
        id: "work",
        label: "直接就業",
        text: "你比同齡人更早明白薪水入帳的重量。",
        effects: { money: 190, stamina: 4, intellect: 2, health: -3 },
        next: "收入提早開始。",
      },
      {
        id: "apprentice",
        label: "拜師學一門手藝",
        text: "你的手開始記得腦袋還說不清的事。",
        effects: { stamina: 6, intellect: 5, charm: 2, money: 40 },
        next: "技能穩定成長。",
      },
      {
        id: "gap",
        label: "打工旅行一年",
        text: "你在陌生城市迷路，也在迷路時長大。",
        effects: { luck: 6, charm: 5, empathy: 4, money: -80 },
        next: "視野變寬，存款變薄。",
      },
    ],
  },
  {
    id: "age-22",
    age: 22,
    title: "研究所、進修或先工作",
    text: "大學畢業不是唯一終點。你要繼續深化專業、邊工作邊進修，還是把時間直接投入職場？",
    choices: [
      {
        id: "graduate-public",
        label: "就讀公立研究所",
        text: "用較可控的學費換取更深的專業與研究經驗。",
        effects: { intellect: 13, stamina: -4, health: -2, money: -150 },
        requires: { money: 150 },
        next: "專業門檻提高，收入與正式職涯再延後幾年。",
      },
      {
        id: "graduate-overseas",
        label: "申請海外研究所",
        text: "跨文化資源與人脈很珍貴，代價是高額學費與離家生活。",
        effects: { intellect: 12, charm: 8, empathy: -2, money: -320 },
        requires: { money: 320 },
        next: "視野大幅擴張，家庭現金流也明顯吃緊。",
      },
      {
        id: "graduate-parttime",
        label: "邊工作邊進修",
        text: "用收入支付部分學費，同時承受工作與課業的雙重負荷。",
        effects: { intellect: 8, stamina: -6, health: -3, money: 45 },
        next: "履歷與能力同步累積，休息時間變得很少。",
      },
      {
        id: "graduate-skip",
        label: "先就業，不讀研究所",
        text: "你把時間直接投入職場，進修保留為未來選項。",
        effects: { stamina: 4, intellect: -1, money: 135 },
        next: "收入提早出現，學術專業暫停累積。",
      },
    ],
  },
  {
    id: "age-24",
    age: 24,
    title: "第一份真正的職涯選擇",
    text: "履歷開始有重量。這次不只選公司大小，也要決定職種、業界與願意承擔的收入風險。",
    choices: [
      {
        id: "corporate",
        label: "科技業產品與營運",
        text: "進入數位產品團隊，在工程、使用者與商業需求之間協調。",
        effects: { money: 260, intellect: 4, health: -4 },
        next: "科技業的穩定薪資開始，也要持續追趕快速變動的工具。",
      },
      {
        id: "finance",
        label: "金融業財務分析",
        text: "用數字、法規與風險判斷支撐每一筆決策。",
        effects: { money: 280, intellect: 6, health: -4, empathy: -1 },
        next: "起薪較高，工時與績效壓力也更清楚。",
      },
      {
        id: "healthcare",
        label: "醫療與照護專業",
        text: "把專業用在真實的人身上，也面對輪班與情緒勞動。",
        effects: { money: 190, intellect: 5, empathy: 6, stamina: -3 },
        next: "專業需求穩定，每一天的責任也很具體。",
      },
      {
        id: "education",
        label: "教育與培訓工作",
        text: "準備課程、陪伴學習，也承擔制度與家長期待。",
        effects: { money: 145, empathy: 7, intellect: 4, health: -1 },
        next: "收入平穩，影響會在學生身上慢慢累積。",
      },
      {
        id: "startup",
        label: "加入新創產品團隊",
        text: "角色邊界很模糊，你可能同時做產品、銷售與客戶支援。",
        effects: { luck: 7, intellect: 6, money: 80, health: -6 },
        next: "現金收入較低，成長速度與公司失敗風險都更高。",
      },
      {
        id: "public",
        label: "政府與公共行政",
        text: "在制度裡處理公共服務，把不確定換成固定薪資與保障。",
        effects: { money: 170, intellect: 6, luck: -1, health: 2 },
        next: "升遷較慢，但家庭能依照可預期的工時安排生活。",
      },
      {
        id: "skilled",
        label: "工程製造與技術職",
        text: "用實作、維修、設計與證照累積難以取代的技術。",
        effects: { money: 185, stamina: 6, intellect: 4, health: -3 },
        next: "技能直接換成收入，工作環境也考驗身體。",
      },
      {
        id: "creator",
        label: "文化內容與自由接案",
        text: "把作品、提案和自己的名字一起變成產品。",
        effects: { charm: 8, luck: 5, money: 40, stamina: -3 },
        next: "收入波動很大，作品、人脈與議價能力會逐年改變結果。",
      },
      {
        id: "founder",
        label: "直接創辦小型事業",
        text: "從第一位客戶、成本和現金流開始，不把創業誤認成一次中獎。",
        effects: { money: -160, charm: 7, luck: 5, stamina: -6, health: -2 },
        requires: { money: 160 },
        next: "短期收入最低，但一條可以逐步擴張的事業路線正式開始。",
      },
    ],
  },
  {
    id: "age-30",
    age: 30,
    title: "生活重心與住所",
    text: "三十歲不一定要定型，但工作、住處與關係都開始留下長期痕跡。",
    choices: [
      {
        id: "make-room",
        label: "替親密關係留空間",
        text: "你不替婚姻設定期限，只開始認真安排能容納另一個人的生活。",
        effects: { empathy: 8, charm: 3, money: -90, health: 2 },
        next: "你更願意面對關係裡的責任；真正的相遇與婚姻仍會由愛情事件決定。",
      },
      {
        id: "single",
        label: "把更多時間留給自己",
        text: "你重新分配工作、家庭與獨處時間。這不會結束既有感情或婚姻，只是把一部分注意力放回自己身上。",
        effects: { intellect: 5, money: 220, charm: 2, empathy: -2 },
        next: "資產累積加快，但陪伴家人的時間減少。",
      },
      {
        id: "home",
        label: "貸款買下小房",
        text: "鑰匙很小，責任很大。",
        effects: { money: -260, health: 3, luck: 2 },
        next: "家族資產開始有根。",
      },
      {
        id: "rent-invest",
        label: "租屋並投入投資",
        text: "你把安全感放進市場，而不是牆壁。",
        effects: { money: 120, luck: 6, health: -2 },
        next: "投資波動增加。",
      },
    ],
  },
  {
    id: "age-38",
    age: 38,
    title: "中年前的資產配置",
    text: "你終於有一點籌碼。問題是，要守，還是攻？",
    choices: [
      {
        id: "index",
        label: "長期指數投資",
        text: "你把衝動交給規則，把時間留給複利。",
        effects: { money: 260, luck: 2, intellect: 2 },
        next: "家族財務更穩健。",
      },
      {
        id: "business",
        label: "開一家小店",
        text: "招牌亮起的那晚，你既驕傲又害怕。",
        effects: { money: -160, charm: 7, luck: 7, stamina: -4 },
        next: "事業支線開啟。",
      },
      {
        id: "insurance",
        label: "保險與緊急預備金",
        text: "你開始替壞日子準備好傘。",
        effects: { health: 6, money: -70, empathy: 3 },
        next: "風險承受力提高。",
      },
      {
        id: "care",
        label: "照顧父母與家人",
        text: "你在家庭責任之間奔跑，累，卻知道自己為何而忙。",
        effects: { empathy: 10, health: -5, money: -90, charm: 2 },
        next: "家庭凝聚力增加。",
      },
    ],
  },
  {
    id: "age-50",
    age: 50,
    title: "人生下半場",
    text: "你開始看見體力的邊界，也看見經驗的價值。",
    choices: [
      {
        id: "mentor",
        label: "成為後輩導師",
        text: "你把走過的彎路整理成別人的捷徑。",
        effects: { empathy: 8, charm: 5, money: 80 },
        next: "聲望與人脈上升。",
      },
      {
        id: "executive",
        label: "挑戰管理高位",
        text: "會議變多，責任也變重。",
        effects: { money: 360, charm: 4, health: -7, stamina: -3 },
        next: "收入大幅提升。",
      },
      {
        id: "second-career",
        label: "轉職做真正喜歡的事",
        text: "你把中年危機改寫成第二次出發。",
        effects: { health: 5, charm: 4, luck: 5, money: -80 },
        next: "幸福感提高。",
      },
      {
        id: "frugal",
        label: "極簡生活提前退休",
        text: "你開始清掉多餘物品，也清掉不必要的焦慮。",
        effects: { health: 6, money: 150, empathy: 2, charm: -1 },
        next: "生活支出下降。",
      },
    ],
  },
  {
    id: "age-65",
    age: 65,
    title: "退休與傳承",
    text: "你要把資源交給下一代，還是先完成自己沒做完的夢？",
    choices: [
      {
        id: "inheritance",
        label: "規劃家族信託",
        text: "你把愛寫進條款，避免未來的人為錢爭吵。",
        effects: { money: -60, empathy: 8, intellect: 4 },
        next: "下一代繼承更順利。",
      },
      {
        id: "travel",
        label: "環遊世界",
        text: "你的護照蓋滿章，心也重新變年輕。",
        effects: { charm: 5, luck: 5, health: 2, money: -180 },
        next: "生命故事更豐富。",
      },
      {
        id: "charity",
        label: "成立小型公益基金",
        text: "你把一部分財富送往看不見的明天。",
        effects: { empathy: 12, charm: 6, money: -220 },
        next: "家族名望提高。",
      },
      {
        id: "garden",
        label: "回到日常照顧身體",
        text: "你種花、散步、煮湯，讓每一天慢下來。",
        effects: { health: 10, stamina: 3, money: -40 },
        next: "晚年更安穩。",
      },
    ],
  },
];

export const RELATIONSHIP_SCENES: RelationshipScene[] = [
  {
    id: "teen-encounter-library",
    kind: "teen-encounter",
    title: "圖書館裡被留下的位置",
    text: "放學後的圖書館快坐滿了，{partner} 把旁邊的書包移開，說可以一起準備考試。你們從一道題目聊到喜歡的歌，回過神時天已經暗了。",
    choices: [
      { id: "teen-library-date", label: "約好每天一起讀書", text: "你想讓這段相處延續下去。", effects: { intellect: 3, charm: 2 }, next: "你們慢慢確認彼此的好感，開始了青澀的交往。", action: "start-dating", affection: 10 },
      { id: "teen-library-message", label: "交換聯絡方式", text: "你不急著說喜歡，只想多認識一點。", effects: { empathy: 2, intellect: 2 }, next: "訊息越聊越長，幾週後你們決定交往看看。", action: "start-dating", affection: 7 },
      { id: "teen-library-friend", label: "維持讀書夥伴", text: "你喜歡這份默契，但還不想談戀愛。", effects: { intellect: 3 }, next: "你多了一位能互相提醒功課的朋友。", action: "stay-single", affection: 0 },
      { id: "teen-library-focus", label: "先專心準備考試", text: "你把心動收好，決定先處理眼前目標。", effects: { intellect: 4, charm: -1 }, next: "這次相遇停在一段溫柔的記憶裡。", action: "stay-single", affection: 0 },
    ],
  },
  {
    id: "teen-encounter-club",
    kind: "teen-encounter",
    title: "社團結束後的操場",
    text: "社團練習結束，{partner} 留下來陪你收器材。夕陽把操場拉得很長，對方問你明天要不要一起走回家。",
    choices: [
      { id: "teen-club-walk", label: "答應一起回家", text: "你假裝很自然，其實一路都在注意彼此的距離。", effects: { charm: 3, stamina: 1 }, next: "回家的路成了固定約會，你們開始交往。", action: "start-dating", affection: 11 },
      { id: "teen-club-group", label: "再找幾位同學同行", text: "你想慢一點，不讓關係一下子承受太多目光。", effects: { empathy: 3, charm: 1 }, next: "相處久了，你們仍走到彼此身邊。", action: "start-dating", affection: 7 },
      { id: "teen-club-friend", label: "笑著說社團見", text: "你沒有同樣的心動，也不想給錯誤暗示。", effects: { empathy: 3 }, next: "你們保持自在的同學關係。", action: "stay-single", affection: 0 },
      { id: "teen-club-avoid", label: "找理由先離開", text: "被注意讓你不知所措。", effects: { health: -1, intellect: 1 }, next: "你錯過了這次機會，也更明白自己的不安。", action: "stay-single", affection: 0 },
    ],
  },
  {
    id: "teen-dating-exam",
    kind: "teen-dating",
    title: "段考前被冷落的訊息",
    text: "你忙著準備考試，連續幾天只用短短幾句回覆 {partner}。對方問是不是做錯了什麼，你才發現沉默也會讓人猜測。",
    choices: [
      { id: "teen-exam-plan", label: "約定考後好好見面", text: "你說明壓力，也給出明確的時間。", effects: { intellect: 3, empathy: 3 }, next: "對方理解了，你們學會在忙碌時仍讓彼此安心。", action: "grow", affection: 7 },
      { id: "teen-exam-study", label: "邀對方一起讀書", text: "你試著讓感情與學業不必互相排斥。", effects: { intellect: 4, charm: 2, stamina: -1 }, next: "讀書效率不一定更高，但關係更靠近。", action: "grow", affection: 6 },
      { id: "teen-exam-ignore", label: "等考完再說", text: "你覺得現在解釋只會浪費時間。", effects: { intellect: 3, empathy: -2 }, next: "成績穩住了，對方的失落卻沒有立刻消失。", action: "grow", affection: -7 },
      { id: "teen-exam-break", label: "認為現在不適合交往", text: "你不想讓彼此在猜疑裡消耗。", effects: { health: -2, intellect: 2 }, next: "你們分開，把時間還給各自的生活。", action: "break-up", affection: -100 },
    ],
  },
  {
    id: "teen-dating-parents",
    kind: "teen-dating",
    title: "被家人發現的交往",
    text: "家人看見你和 {partner} 的訊息，擔心戀愛影響學業，也提出比你預期更嚴格的限制。你第一次要在家庭信任與自己的感情之間協調。",
    choices: [
      { id: "teen-parents-talk", label: "坦白介紹對方與相處規則", text: "你願意讓家人了解，也承諾守住學業與安全界線。", effects: { empathy: 5, intellect: 2 }, next: "限制仍在，但大人開始願意聽你說。", action: "grow", affection: 8 },
      { id: "teen-parents-secret", label: "轉為偷偷交往", text: "你害怕關係被迫結束，只能把每次見面藏起來。", effects: { charm: 2, health: -3 }, next: "秘密讓相處刺激，也讓壓力越來越大。", action: "grow", affection: 2 },
      { id: "teen-parents-pause", label: "暫停約會直到學期結束", text: "你們保留承諾，先讓生活回到穩定。", effects: { intellect: 4, empathy: 2 }, next: "距離考驗關係，也讓你們更清楚是否真的在乎。", action: "grow", affection: -2 },
      { id: "teen-parents-end", label: "接受家人的要求分手", text: "你不想每天說謊，也還沒有能力獨立承擔後果。", effects: { health: -3, empathy: 2 }, next: "這段初戀結束了，留下很久才淡去的難過。", action: "break-up", affection: -100 },
    ],
  },
  {
    id: "encounter-rain",
    kind: "encounter",
    title: "雨停之前的談話",
    text: "一場突來的雨把你和 {partner} 留在同一個屋簷下。你們從附近哪家店好吃，聊到各自想過什麼樣的生活。雨已經小了，對方卻沒有急著離開。",
    choices: [
      { id: "rain-date", label: "主動約下次見面", text: "你說出那句有點冒險的邀請。", effects: { charm: 3, luck: 2 }, next: "對方笑著答應，你們開始認真約會。", action: "start-dating", affection: 12 },
      { id: "rain-slow", label: "先交換聯絡方式", text: "你想再認識久一點，不急著替關係命名。", effects: { empathy: 3, intellect: 1 }, next: "幾次訊息與散步後，你們決定試著交往。", action: "start-dating", affection: 7 },
      { id: "rain-friend", label: "把這次留成朋友", text: "你喜歡這段談話，但沒有戀愛的心動。", effects: { empathy: 2, charm: 1 }, next: "你們偶爾聯絡，人生仍保持單身。", action: "stay-single", affection: 0 },
      { id: "rain-leave", label: "禮貌道別", text: "你現在沒有餘裕開始一段關係。", effects: { stamina: 1 }, next: "雨停了，你們走向不同方向。", action: "stay-single", affection: 0 },
    ],
  },
  {
    id: "encounter-project",
    kind: "encounter",
    title: "一起收拾殘局的人",
    text: "臨時活動出了一連串小狀況，你和 {partner} 留到最後收尾。忙完時已經很晚，對方遞來一杯飲料，說和你合作很安心。",
    choices: [
      { id: "project-supper", label: "提議一起吃宵夜", text: "你想知道工作以外的對方是什麼樣子。", effects: { charm: 4, stamina: -1 }, next: "宵夜吃得很慢，後來成了你們第一次約會。", action: "start-dating", affection: 11 },
      { id: "project-message", label: "傳訊息延續話題", text: "你沒有急著表白，只讓對話自然地繼續。", effects: { intellect: 2, empathy: 2 }, next: "幾週後，你們確認了彼此的好感。", action: "start-dating", affection: 8 },
      { id: "project-colleague", label: "維持合作關係", text: "你珍惜默契，但不想把它變成戀愛。", effects: { intellect: 2, charm: 1 }, next: "你多了一位可靠的朋友。", action: "stay-single", affection: 0 },
      { id: "project-distance", label: "刻意保持距離", text: "你察覺到好感，也清楚自己還沒準備好。", effects: { empathy: 1, health: 1 }, next: "心動沒有消失，只是沒有被你追上。", action: "stay-single", affection: 0 },
    ],
  },
  {
    id: "encounter-friend",
    kind: "encounter",
    title: "朋友帶來的新面孔",
    text: "一場普通聚會裡，{partner} 記得你隨口提過的小事。散場後，你們並肩走了一段路，沉默也不尷尬。",
    choices: [
      { id: "friend-honest", label: "坦白說想再見面", text: "你不繞彎，直接表達好感。", effects: { charm: 5, luck: 1 }, next: "真誠得到回應，你們開始交往。", action: "start-dating", affection: 13 },
      { id: "friend-group", label: "再約一次朋友聚會", text: "你希望在自然的節奏裡確認感覺。", effects: { empathy: 3, charm: 2 }, next: "幾次相處後，關係慢慢跨過朋友的界線。", action: "start-dating", affection: 8 },
      { id: "friend-only", label: "只把對方當朋友", text: "舒服不一定就是愛情。", effects: { empathy: 3 }, next: "你們成了能聊天的朋友。", action: "stay-single", affection: 0 },
      { id: "friend-focus", label: "把重心留給自己", text: "你正想完成一件重要的事，暫時不展開戀愛。", effects: { intellect: 3, stamina: 1 }, next: "你回到自己的生活安排。", action: "stay-single", affection: 0 },
    ],
  },
  {
    id: "reunion-message",
    kind: "reunion",
    title: "舊訊息重新亮起",
    text: "很久沒有聯絡的 {partner} 傳來一則訊息。對方沒有假裝過去不存在，只問你願不願意找一天坐下來，看看現在的彼此是否已經不同。",
    choices: [
      { id: "reunion-try-again", label: "見面後重新交往", text: "你們先談當初為何分開，也說清楚這次不能再逃避的問題。", effects: { empathy: 5, charm: 2, health: 1 }, next: "熟悉感沒有消失，但這次你們決定用新的方式重新開始。", action: "start-dating", affection: 8 },
      { id: "reunion-slow", label: "先恢復聯絡，再慢慢確認", text: "你不把懷念直接當成答案，只約定用一段時間觀察彼此。", effects: { intellect: 3, empathy: 4 }, next: "幾次坦白的談話後，你們仍決定再試一次。", action: "start-dating", affection: 4 },
      { id: "reunion-goodbye", label: "好好回覆，但不再重來", text: "你承認那段感情的重要，也清楚自己已經走向不同生活。", effects: { empathy: 4, health: 2 }, next: "你們把沒有說完的話說完，這次真正向彼此道別。", action: "stay-single", affection: 0 },
    ],
  },
  {
    id: "dating-weekend",
    kind: "dating",
    title: "同一個週末，兩種期待",
    text: "{partner} 想把難得的週末留給兩個人，你卻已經累到只想獨處。這不是大爭吵，卻很像未來生活的縮影。",
    choices: [
      { id: "weekend-talk", label: "說清楚再一起安排", text: "你承認疲憊，也問對方真正期待的是什麼。", effects: { empathy: 4, health: 1 }, next: "你們找到既能休息也能相處的方式。", action: "grow", affection: 8 },
      { id: "weekend-date", label: "打起精神赴約", text: "你先陪對方完成期待，再替自己留一個安靜晚上。", effects: { charm: 3, stamina: -2 }, next: "約會很愉快，但你也學到不能總靠勉強。", action: "grow", affection: 4 },
      { id: "weekend-alone", label: "堅持各過各的", text: "你需要界線，卻沒有好好解釋。", effects: { stamina: 2, empathy: -2 }, next: "你休息到了，關係裡卻留下一點失落。", action: "grow", affection: -7 },
      { id: "weekend-end", label: "承認彼此節奏不合", text: "你不想讓小摩擦累積成長期怨氣。", effects: { health: -2, intellect: 2 }, next: "你們難過地分開，仍尊重一起走過的時間。", action: "break-up", affection: -100 },
    ],
  },
  {
    id: "dating-career",
    kind: "dating",
    title: "一個不能忽略的工作機會",
    text: "{partner} 收到外地發展的機會，至少要離開一年。餐桌上放著還沒吃完的晚餐，你們第一次必須把愛與各自的人生放在同一張表上衡量。",
    choices: [
      { id: "career-distance", label: "嘗試遠距交往", text: "你們訂下見面與聯絡的節奏，也允許彼此有脆弱的時候。", effects: { empathy: 5, intellect: 2, money: -40 }, next: "距離很難，但你們的信任變得具體。", action: "grow", affection: 7 },
      { id: "career-follow", label: "一起規劃搬遷", text: "你願意調整部分人生，但要求這必須是共同決定。", effects: { luck: 5, money: -100, charm: 2 }, next: "新的城市讓關係更靠近，也帶來新的壓力。", action: "grow", affection: 10 },
      { id: "career-stay", label: "請對方留下", text: "你誠實表達不安，卻把選擇的重量交給了對方。", effects: { charm: -1, empathy: -2 }, next: "機會被放棄，這件事暫時沒有真正過去。", action: "grow", affection: -9 },
      { id: "career-goodbye", label: "不互相犧牲，和平分手", text: "你們都不想用愛要求對方縮小人生。", effects: { empathy: 4, health: -3 }, next: "離別很痛，但沒有誰成為壞人。", action: "break-up", affection: -100 },
    ],
  },
  {
    id: "dating-family",
    kind: "dating",
    title: "第一次走進彼此的家庭",
    text: "你陪 {partner} 回家吃飯。長輩的問題有些熱情、有些越界；回程路上，對方問你今天是不是很不自在。",
    choices: [
      { id: "family-honest", label: "誠實談界線", text: "你沒有批評家人，只說明哪些事讓自己有壓力。", effects: { empathy: 5, charm: 2 }, next: "對方願意站在你身邊，你也更理解這個家庭。", action: "grow", affection: 9 },
      { id: "family-adapt", label: "努力融入", text: "你記住每個人的習慣，也主動幫忙收拾。", effects: { charm: 4, stamina: -2 }, next: "家人喜歡你，但你還需要學會表達自己的感受。", action: "grow", affection: 5 },
      { id: "family-dismiss", label: "說沒事，之後再疏遠", text: "你不想衝突，於是把不舒服藏起來。", effects: { empathy: -2, health: -1 }, next: "表面平靜，關係裡卻多了一個不能談的角落。", action: "grow", affection: -6 },
      { id: "family-break", label: "認為價值觀差太多", text: "家庭邊界對你太重要，你不願賭它之後會自然消失。", effects: { intellect: 2, health: -2 }, next: "你們結束關係，各自整理失落。", action: "break-up", affection: -100 },
    ],
  },
  {
    id: "proposal-future",
    kind: "proposal",
    title: "談到五年後的那一晚",
    text: "你和 {partner} 整理帳單時，話題一路走到住在哪裡、要不要孩子、怎麼照顧父母。對方問：『你想像的未來裡，有我嗎？』",
    choices: [
      { id: "future-propose", label: "認真求婚", text: "你沒有準備華麗台詞，只清楚說出願意共同承擔什麼。", effects: { empathy: 7, charm: 4, money: -60 }, next: "對方答應了。你們開始籌備婚姻，而不只是婚禮。", action: "propose", affection: 12 },
      { id: "future-plan", label: "先完成共同生活計畫", text: "你提出先同住、存一筆預備金，再決定婚期。", effects: { intellect: 4, money: 80, empathy: 2 }, next: "承諾沒有戒指，卻有清楚的時間表。", action: "grow", affection: 5 },
      { id: "future-unsure", label: "坦白自己還不確定", text: "你不拿模糊承諾安撫對方。", effects: { empathy: 2, charm: -1 }, next: "對方失望，但願意再給關係一些時間。", action: "grow", affection: -5 },
      { id: "future-end", label: "承認未來方向不同", text: "愛並沒有消失，但你們想去的地方不再相同。", effects: { health: -4, empathy: 3 }, next: "你們好好道別，沒有把多年相處變成互相指責。", action: "break-up", affection: -100 },
    ],
  },
  {
    id: "engagement-wedding",
    kind: "engagement",
    title: "婚姻從哪一天開始",
    text: "你和 {partner} 攤開預算、家人名單與生活安排。真正困難的不是選場地，而是決定哪些期待屬於你們，哪些只是別人的聲音。",
    choices: [
      { id: "wedding-simple", label: "簡單登記，留錢過生活", text: "你們只邀最親近的人吃飯，把預算留給共同住處。", effects: { money: -80, health: 3, empathy: 5 }, next: "沒有盛大排場，但那天很像你們。", action: "marry", affection: 8 },
      { id: "wedding-family", label: "辦一場家人期待的婚禮", text: "你們花很多時間協調，也讓兩個家庭正式認識。", effects: { money: -220, charm: 6, stamina: -3 }, next: "疲憊之後，家族樹多了一條清楚的連線。", action: "marry", affection: 5 },
      { id: "wedding-trip", label: "旅行中交換誓言", text: "你們把儀式縮到只剩彼此與真正想說的話。", effects: { money: -150, luck: 5, health: 2 }, next: "回家後，你們開始以夫妻的方式安排日常。", action: "marry", affection: 7 },
      { id: "wedding-wait", label: "延後婚期，再磨合一年", text: "你們不取消承諾，只是不在焦慮裡做決定。", effects: { intellect: 2, empathy: 2 }, next: "婚期暫緩，關係需要下一次坦白。", action: "postpone", affection: -2 },
    ],
  },
  {
    id: "married-chores",
    kind: "married",
    title: "沒有人看見的家務",
    text: "工作忙起來後，冰箱、垃圾與帳單像會自行繁殖。{partner} 說自己一直在收尾，你也覺得並沒有少做。爭執其實不是那只沒洗的碗。",
    choices: [
      { id: "chores-list", label: "把所有家務列出來重分", text: "你們連記得買衛生紙這種隱形工作也算進去。", effects: { intellect: 2, empathy: 5 }, next: "分工不再靠猜，家裡安靜了許多。", action: "grow", affection: 7 },
      { id: "chores-help", label: "主動多做一段時間", text: "你先接過對方最疲憊的部分，再約時間重談。", effects: { stamina: -3, empathy: 4 }, next: "對方感受到支持，但長期分工仍需要處理。", action: "grow", affection: 4 },
      { id: "chores-service", label: "花錢購買家務服務", text: "你們承認時間也是資源。", effects: { money: -80, health: 3 }, next: "衝突少了，財務規劃多了一筆固定支出。", action: "grow", affection: 5 },
      { id: "chores-ignore", label: "覺得過幾天就沒事", text: "你避開談話，把問題留給下一次爆發。", effects: { empathy: -4, health: -2 }, next: "日子繼續過，關係的溫度卻低了一點。", action: "grow", affection: -9 },
    ],
  },
  {
    id: "married-parents",
    kind: "married",
    title: "兩邊父母都需要你們",
    text: "你的家人需要陪診，{partner} 的家裡也剛好出了狀況。時間與錢都不夠，你們必須決定怎麼分配，而不是比較誰的家人更重要。",
    choices: [
      { id: "parents-plan", label: "一起排出照顧計畫", text: "你們找其他親人、醫療與社福資源分擔。", effects: { intellect: 3, empathy: 6, money: -70 }, next: "事情仍辛苦，但你們像同一隊。", action: "grow", affection: 8 },
      { id: "parents-mine", label: "先處理自己的家人", text: "你認為眼前的急迫性不能等。", effects: { empathy: 2, stamina: -3 }, next: "危機暫時度過，伴侶卻覺得被留在後面。", action: "grow", affection: -5 },
      { id: "parents-partner", label: "先支持伴侶的家人", text: "你主動承擔家裡其他工作，讓對方放心處理。", effects: { empathy: 6, health: -2 }, next: "這份支持被對方牢牢記住。", action: "grow", affection: 7 },
      { id: "parents-money", label: "用存款換取照護支援", text: "你們請專業人員協助，保留彼此喘息的空間。", effects: { money: -160, health: 4, empathy: 3 }, next: "財務變緊，關係卻沒有被耗盡。", action: "grow", affection: 5 },
    ],
  },
  {
    id: "married-dream",
    kind: "married",
    title: "被擱置很久的夢",
    text: "{partner} 說想暫停工作，完成一件惦記多年的事。那會減少家庭收入，也可能讓對方重新找回自己。你知道這不是一句『支持就好』能解決的事。",
    choices: [
      { id: "dream-budget", label: "一起做半年試行計畫", text: "你們設定預算、期限與檢視點，讓夢想有落地的方法。", effects: { intellect: 3, empathy: 5, money: -90 }, next: "冒險有了邊界，也有了真正的支持。", action: "grow", affection: 8 },
      { id: "dream-full", label: "全力支持對方", text: "你願意暫時多扛一些，因為相信這件事的重要。", effects: { empathy: 7, money: -150, stamina: -3 }, next: "生活變緊，但你們更靠近。", action: "grow", affection: 9 },
      { id: "dream-delay", label: "請對方等財務更穩", text: "你的理由務實，卻讓對方擔心夢想會永遠排在最後。", effects: { money: 100, empathy: -2 }, next: "計畫被收回抽屜，沒有真正消失。", action: "grow", affection: -5 },
      { id: "dream-refuse", label: "反對破壞現有生活", text: "你把穩定放在第一位，也關上了對話。", effects: { money: 130, charm: -2, empathy: -4 }, next: "帳目更安全，兩人之間卻多了一段距離。", action: "grow", affection: -10 },
    ],
  },
  {
    id: "adoption-social-worker",
    kind: "adoption",
    title: "社工帶來的一份家庭資料",
    text: "你和 {partner} 談過幾次要不要成為父母。這天，社工說有一名嬰兒正在等待長期家庭，也提醒你們領養不是替代方案，而是一生都要誠實面對孩子身世的承諾。",
    choices: [
      { id: "adoption-welcome", label: "提出正式領養申請", text: "你們一起完成評估、訪談與居家準備。", effects: { empathy: 9, money: -180, health: -1 }, next: "幾個月後，一個孩子正式成為你們的家人。", action: "adopt", affection: 10 },
      { id: "adoption-foster", label: "先從寄養轉領養開始", text: "你們接受更長的磨合與觀察期，不急著要求孩子立刻把這裡當成家。", effects: { empathy: 11, money: -120, stamina: -3 }, next: "信任一點一點建立，孩子最後留在了這個家庭。", action: "adopt", affection: 9 },
      { id: "adoption-prepare", label: "先完成財務與照顧準備", text: "你們列出托育、工作與支援人力，決定準備好再申請。", effects: { intellect: 4, money: 80, empathy: 2 }, next: "領養計畫被保留下來，等待更合適的時機。", action: "grow", affection: 4 },
      { id: "adoption-decline", label: "決定維持兩人的生活", text: "你們坦白承認並不想成為父母，不用孩子替關係完成某種標準答案。", effects: { empathy: 5, health: 2 }, next: "決定很清楚，你們也更理解彼此想要的家庭。", action: "grow", affection: 5 },
    ],
  },
  {
    id: "adoption-family-meeting",
    kind: "adoption",
    title: "第一次見到等待家庭的孩子",
    text: "機構安排你和 {partner} 參加說明與會面。孩子抓著照顧者的衣角，沒有立刻靠近。你們忽然明白，成為父母不是把孩子帶回家，而是讓對方慢慢相信不會再被放下。",
    choices: [
      { id: "adoption-patient", label: "願意按孩子的速度建立關係", text: "你們接受親職訓練，也替未來可能出現的分離焦慮尋求協助。", effects: { empathy: 12, money: -160, stamina: -2 }, next: "漫長程序結束後，孩子有了新的家與名字。", action: "adopt", affection: 11 },
      { id: "adoption-support", label: "建立支援網後提出申請", text: "你們先找好家人、托育和諮商資源，不讓照顧只落在其中一人身上。", effects: { intellect: 6, empathy: 7, money: -220 }, next: "準備讓磨合更穩，孩子正式加入家庭。", action: "adopt", affection: 9 },
      { id: "adoption-later", label: "承認現在還沒有餘裕", text: "你們不因一時感動做出無法承擔的承諾。", effects: { intellect: 3, empathy: 4 }, next: "這次沒有申請，但你們把需要準備的事看得更清楚。", action: "grow", affection: 3 },
      { id: "adoption-donate", label: "不領養，改為長期支持機構", text: "你們選擇用固定捐款與志工時間支持更多孩子。", effects: { empathy: 8, charm: 3, money: -70 }, next: "家庭沒有增加成員，卻和社區建立了長期連結。", action: "grow", affection: 5 },
    ],
  },
];

const CORE_RANDOM_EVENTS: GameEvent[] = [
  {
    id: "toddler-first-steps",
    title: "搖搖晃晃的第一步",
    text: "你扶著桌角往前走了三步，家人像看見奇蹟一樣鼓掌。",
    maxAge: 3,
    weight: 8,
    effects: { stamina: 3, health: 2 },
    tags: ["childhood", "family"],
  },
  {
    id: "toddler-picture-book",
    title: "睡前繪本",
    text: "你指著書上的月亮咿咿呀呀，大人一遍又一遍念給你聽。",
    maxAge: 5,
    weight: 8,
    effects: { intellect: 3, empathy: 1 },
    tags: ["childhood", "growth"],
  },
  {
    id: "toddler-fever",
    title: "半夜發燒",
    text: "你在夜裡哭醒，家人輪流量體溫、換毛巾，天亮後終於退燒。",
    maxAge: 5,
    weight: 5,
    effects: { health: -4, empathy: 1 },
    tags: ["childhood", "health"],
  },
  {
    id: "kindergarten-sharing",
    title: "幼兒園分享日",
    text: "你把最喜歡的小玩具借給同學，第一次學會分享也會捨不得。",
    minAge: 3,
    maxAge: 6,
    weight: 7,
    effects: { empathy: 3, charm: 1 },
    tags: ["childhood", "school"],
  },
  {
    id: "family-park-day",
    title: "公園野餐",
    text: "你在草地上追泡泡，累了就靠在家人腿上睡著。",
    minAge: 2,
    maxAge: 8,
    weight: 7,
    effects: { stamina: 2, health: 2, empathy: 1 },
    tags: ["childhood", "family"],
  },
  {
    id: "grandparent-story",
    title: "長輩講古",
    text: "你聽不懂每個細節，卻記住了長輩說故事時溫柔的聲音。",
    minAge: 4,
    maxAge: 12,
    weight: 6,
    effects: { empathy: 3, intellect: 1 },
    tags: ["childhood", "family"],
  },
  {
    id: "lottery-small",
    title: "便利商店發票中獎",
    text: "你差點把發票丟掉，卻在最後一刻對中一筆小獎。",
    minAge: 18,
    weight: 5,
    effects: { money: 80, luck: 2 },
    tags: ["money", "luck"],
  },
  {
    id: "lottery-big",
    title: "意外的大獎",
    text: "那組隨手買的號碼竟然中了。親戚突然都記得你的生日。",
    minAge: 18,
    weight: 1,
    effects: { money: 900, luck: 8, charm: 2 },
    tags: ["money", "rare"],
  },
  {
    id: "traffic-accident",
    title: "雨天車禍",
    text: "濕滑路口的一聲急煞，讓你住院好幾週。",
    minAge: 18,
    weight: 3,
    effects: { health: -16, stamina: -6, money: -120 },
    tags: ["health", "crisis"],
  },
  {
    id: "flu",
    title: "流感季",
    text: "你以為只是累，結果連續燒了三天。",
    minAge: 6,
    weight: 8,
    effects: { health: -7, stamina: -4 },
    tags: ["health"],
  },
  {
    id: "mentor",
    title: "遇見貴人",
    text: "一位前輩在你最迷惘時聽你說完計畫，介紹你認識能提供實習或作品建議的人。",
    minAge: 16,
    weight: 5,
    effects: { intellect: 4, charm: 3, luck: 3 },
    tags: ["career"],
  },
  {
    id: "burnout",
    title: "過勞警訊",
    text: "某天早上你盯著天花板，突然不想起床。",
    minAge: 22,
    weight: 5,
    effects: { health: -10, stamina: -5, money: 60 },
    tags: ["career", "health"],
  },
  {
    id: "viral-post",
    title: "一篇貼文爆紅",
    text: "你寫下真心話，醒來發現陌生人都在轉發。",
    minAge: 15,
    weight: 4,
    effects: { charm: 8, luck: 4, money: 70 },
    tags: ["social"],
  },
  {
    id: "market-crash",
    title: "市場急跌",
    text: "新聞主播語氣平穩，但你的投資帳戶一點也不平穩。",
    minAge: 28,
    weight: 4,
    effects: { money: -220, intellect: 2 },
    tags: ["investment"],
  },
  {
    id: "market-rally",
    title: "景氣回暖",
    text: "你先前忍住沒賣的資產，終於等到一陣順風。",
    minAge: 28,
    weight: 4,
    effects: { money: 260, luck: 3 },
    tags: ["investment"],
  },
  {
    id: "family-illness",
    title: "家人需要照顧",
    text: "一通電話打亂所有安排，你開始重新排序人生。",
    minAge: 25,
    weight: 5,
    effects: { empathy: 7, money: -110, health: -3 },
    tags: ["family"],
  },
  {
    id: "scholarship",
    title: "獎學金通知",
    text: "信箱裡躺著一封好消息，你的努力被看見了。",
    minAge: 12,
    maxAge: 26,
    weight: 4,
    effects: { money: 120, intellect: 4 },
    tags: ["school"],
  },
  {
    id: "friendship",
    title: "老朋友伸出援手",
    text: "你沒有開口，對方卻知道你需要幫忙。",
    minAge: 10,
    weight: 6,
    effects: { empathy: 4, charm: 2, health: 2 },
    tags: ["relationship"],
  },
  {
    id: "scam",
    title: "投資詐騙",
    text: "對方說保證獲利，後來連訊息頭像都消失。",
    minAge: 20,
    weight: 3,
    effects: { money: -180, intellect: 2, luck: -2 },
    tags: ["money", "crisis"],
  },
  {
    id: "promotion",
    title: "升遷提案通過",
    text: "你準備很久的方案終於被採納，職稱也跟著變長。",
    minAge: 25,
    maxAge: 60,
    weight: 5,
    effects: { money: 210, charm: 4, health: -2 },
    tags: ["career"],
  },
  {
    id: "pet",
    title: "收養一位毛茸茸室友",
    text: "牠不會說話，卻讓家裡多了一種等你回來的安靜。",
    minAge: 18,
    weight: 4,
    effects: { empathy: 6, health: 3, money: -60 },
    tags: ["life"],
  },
  {
    id: "night-school",
    title: "夜間進修",
    text: "下班後的教室很亮，你累得要命，卻覺得自己還在長大。",
    minAge: 20,
    maxAge: 55,
    weight: 5,
    effects: { intellect: 7, stamina: -3, money: -70 },
    tags: ["school"],
  },
  {
    id: "inheritance",
    title: "遠親留下遺產",
    text: "你幾乎不記得那位長輩，卻收到一筆意外安排。",
    minAge: 30,
    weight: 2,
    effects: { money: 420, empathy: 1 },
    tags: ["family", "money"],
  },
  {
    id: "lawsuit",
    title: "合約糾紛",
    text: "你第一次發現小字真的很重要。",
    minAge: 24,
    weight: 2,
    effects: { money: -240, intellect: 3, health: -3 },
    tags: ["career", "crisis"],
  },
  {
    id: "meditation",
    title: "開始固定散步冥想",
    text: "你沒有突然變成聖人，只是比較能睡了。",
    minAge: 16,
    weight: 6,
    effects: { health: 6, empathy: 2, stamina: 2 },
    tags: ["health"],
  },
  {
    id: "creative-prize",
    title: "作品得獎",
    text: "評審提到你的名字時，你假裝鎮定，手心卻全是汗。",
    minAge: 16,
    weight: 3,
    effects: { charm: 8, money: 100, luck: 2 },
    tags: ["creative"],
  },
  {
    id: "housing-boom",
    title: "房價上漲",
    text: "你所在的街區突然變熱門，咖啡店比早餐店還多。",
    minAge: 32,
    weight: 3,
    effects: { money: 300, luck: 2 },
    tags: ["asset"],
  },
  {
    id: "rent-hike",
    title: "租金調漲",
    text: "房東傳來訊息，語氣客氣，數字很不客氣。",
    minAge: 20,
    weight: 5,
    effects: { money: -90, health: -2 },
    tags: ["asset"],
  },
  {
    id: "child-born",
    title: "新生命加入家庭",
    text: "凌晨三點的哭聲讓你崩潰，也讓你第一次懂得無條件。",
    minAge: 28,
    maxAge: 45,
    weight: 3,
    effects: { empathy: 10, money: -150, health: -3, charm: 2 },
    tags: ["family"],
  },
  {
    id: "health-check",
    title: "健康檢查及早發現",
    text: "報告上的紅字讓你緊張，也幸好還來得及。",
    minAge: 35,
    weight: 4,
    effects: { health: 8, money: -70, intellect: 1 },
    tags: ["health"],
  },
  {
    id: "community",
    title: "社區志工活動",
    text: "你原本只是幫忙搬椅子，後來認識了整條街的人。",
    minAge: 14,
    weight: 5,
    effects: { empathy: 6, charm: 4, stamina: -1 },
    tags: ["social"],
  },
  {
    id: "data-loss",
    title: "硬碟壞掉",
    text: "你聽見喀的一聲，所有沒備份的檔案都變成教訓。",
    minAge: 14,
    weight: 3,
    effects: { intellect: 2, money: -70, health: -1 },
    tags: ["life"],
  },
  {
    id: "side-hustle",
    title: "副業接到大案",
    text: "一封陌生來信，讓你的週末突然值錢起來。",
    minAge: 22,
    maxAge: 58,
    weight: 5,
    effects: { money: 230, stamina: -4, charm: 3 },
    tags: ["career"],
  },
  {
    id: "tax",
    title: "補稅通知",
    text: "信封很薄，內容很重。",
    minAge: 25,
    weight: 3,
    effects: { money: -130, intellect: 1 },
    tags: ["money"],
  },
  {
    id: "festival",
    title: "地方祭典",
    text: "燈籠亮起，你在人群裡感到自己屬於某個地方。",
    weight: 6,
    effects: { charm: 3, empathy: 3, health: 1 },
    tags: ["life"],
  },
  {
    id: "recession",
    title: "公司縮編",
    text: "會議室裡的空氣很冷，大家都假裝還能專心。",
    minAge: 24,
    maxAge: 60,
    weight: 3,
    effects: { money: -200, health: -6, luck: -2 },
    tags: ["career", "crisis"],
  },
  {
    id: "book",
    title: "一本書改變觀點",
    text: "你在書店隨手翻開，卻像被未來的自己拍了拍肩。",
    minAge: 8,
    weight: 6,
    effects: { intellect: 5, empathy: 2 },
    tags: ["growth"],
  },
  {
    id: "sports-habit",
    title: "養成運動習慣",
    text: "一開始只是十分鐘，後來你開始期待流汗。",
    minAge: 10,
    weight: 5,
    effects: { stamina: 7, health: 6 },
    tags: ["health"],
  },
  {
    id: "bad-loan",
    title: "替人作保出事",
    text: "你以為只是幫忙簽名，現實卻開始催款。",
    minAge: 25,
    weight: 2,
    effects: { money: -320, empathy: -3, health: -4 },
    tags: ["money", "crisis"],
  },
  {
    id: "patent",
    title: "點子被買下",
    text: "你在便條紙上的草圖，最後變成一筆授權金。",
    minAge: 24,
    weight: 2,
    effects: { money: 500, intellect: 5, luck: 4 },
    tags: ["career", "rare"],
  },
  {
    id: "reunion",
    title: "同學會",
    text: "你看見很多版本的人生，忽然比較不急著證明什麼。",
    minAge: 30,
    weight: 4,
    effects: { empathy: 5, charm: 4, health: 1 },
    tags: ["relationship"],
  },
  {
    id: "elder-wisdom",
    title: "長輩的一句話",
    text: "那句話當下普通，幾年後卻在你心裡發亮。",
    minAge: 4,
    weight: 4,
    effects: { empathy: 4, intellect: 3 },
    tags: ["family"],
  },
  {
    id: "pandemic-memory",
    title: "城市暫停",
    text: "街道安靜得不真實，你學會珍惜可見面的日子。",
    minAge: 8,
    weight: 2,
    effects: { health: -4, empathy: 6, money: -80 },
    tags: ["world"],
  },
  {
    id: "ai-boost",
    title: "新工具讓效率翻倍",
    text: "你把繁瑣工作交給工具，終於有空思考真正重要的事。",
    minAge: 18,
    weight: 4,
    effects: { intellect: 5, money: 120, luck: 2 },
    tags: ["technology"],
  },
  {
    id: "quiet-year",
    title: "平靜的一年",
    text: "沒有大事發生。後來你才知道，這也是一種禮物。",
    weight: 9,
    effects: { health: 2, empathy: 1 },
    tags: ["life"],
  },
  {
    id: "medical-breakthrough",
    title: "新療法奏效",
    text: "醫生笑著說指數好很多，你把那句話記了很久。",
    minAge: 55,
    weight: 3,
    effects: { health: 12, money: -120, luck: 3 },
    tags: ["health"],
  },
  {
    id: "baby-night-fever",
    title: "半夜突然發燒",
    text: "你哭得停不下來，家人輪流量體溫、換毛巾，直到天亮才鬆一口氣。",
    minAge: 1,
    maxAge: 3,
    weight: 6,
    effects: { health: -4, empathy: 2 },
    tags: ["childhood", "health"],
  },
  {
    id: "baby-first-word",
    title: "第一個清楚的詞",
    text: "你突然叫出一個熟悉的稱呼，大人愣了一秒，接著整間屋子都在笑。",
    minAge: 1,
    maxAge: 3,
    weight: 6,
    effects: { intellect: 3, charm: 2 },
    tags: ["childhood", "family"],
  },
  {
    id: "baby-daycare",
    title: "第一次留在托育中心",
    text: "門關上時你哭得很大聲，下午卻已經學會跟著別人收玩具。",
    minAge: 2,
    maxAge: 3,
    weight: 5,
    effects: { empathy: 3, stamina: 1, health: -1 },
    tags: ["childhood", "growth"],
  },
  {
    id: "baby-pediatric-check",
    title: "例行兒童健檢",
    text: "醫師仔細檢查成長曲線，提醒家人睡眠、飲食與疫苗一樣不能省略。",
    minAge: 1,
    maxAge: 3,
    weight: 5,
    effects: { health: 5, money: -25 },
    tags: ["childhood", "health"],
  },
  {
    id: "baby-playground-fall",
    title: "遊戲場上的擦傷",
    text: "你跑得太快跌在地上，膝蓋很痛，卻也第一次自己站了起來。",
    minAge: 2,
    maxAge: 4,
    weight: 5,
    effects: { health: -2, stamina: 3 },
    tags: ["childhood", "growth"],
  },
  {
    id: "baby-separation",
    title: "看不見家人的那幾分鐘",
    text: "你在商店裡一轉身找不到熟悉的人，短短幾分鐘像一整天那麼長。",
    minAge: 2,
    maxAge: 4,
    weight: 3,
    effects: { health: -2, empathy: 3 },
    tags: ["childhood", "family"],
  },
  {
    id: "kindergarten-show",
    title: "幼兒園成果表演",
    text: "你忘了半段動作，最後仍跟著大家一起鞠躬，台下掌聲比想像中熱烈。",
    minAge: 4,
    maxAge: 6,
    weight: 6,
    effects: { charm: 4, stamina: 1 },
    tags: ["childhood", "school"],
  },
  {
    id: "kindergarten-friend",
    title: "第一位主動牽手的朋友",
    text: "午睡前有人把毯子分給你一角，你們從此常常排在同一隊。",
    minAge: 4,
    maxAge: 6,
    weight: 6,
    effects: { empathy: 4, charm: 2 },
    tags: ["childhood", "friendship"],
  },
  {
    id: "kindergarten-lost-toy",
    title: "最喜歡的玩具不見了",
    text: "你翻遍書包和教室，最後學會有些珍惜不是哭得夠久就能找回來。",
    minAge: 4,
    maxAge: 6,
    weight: 4,
    effects: { empathy: 3, health: -1 },
    tags: ["childhood", "growth"],
  },
  {
    id: "kindergarten-tooth",
    title: "第一顆乳牙掉了",
    text: "舌頭一直忍不住去碰那個空位，家人把小小的牙齒收進盒子裡。",
    minAge: 5,
    maxAge: 7,
    weight: 5,
    effects: { health: 2, luck: 1 },
    tags: ["childhood", "health"],
  },
  {
    id: "kindergarten-wall-art",
    title: "畫到牆上的大作",
    text: "你覺得白牆需要一輪太陽，大人卻花了整晚思考該先生氣還是先拍照。",
    minAge: 4,
    maxAge: 6,
    weight: 4,
    effects: { charm: 3, intellect: 2, money: -20 },
    tags: ["childhood", "creative"],
  },
  {
    id: "school-readiness",
    title: "書包比背還寬",
    text: "入學用品一件件放上桌，你開始理解明年會有一個完全不同的日常。",
    minAge: 5,
    maxAge: 6,
    weight: 5,
    effects: { intellect: 3, money: -35, stamina: 1 },
    tags: ["childhood", "school"],
  },
  {
    id: "school-class-election",
    title: "第一次班級幹部選舉",
    text: "你站在講台上說完準備好的話，才發現被同學注視和被認同是兩件不同的事。",
    minAge: 7,
    maxAge: 12,
    weight: 4,
    effects: { charm: 4, empathy: 2 },
    tags: ["school", "social"],
  },
  {
    id: "school-math-contest",
    title: "校內解題比賽",
    text: "最後一題沒有完全算完，但你第一次享受到把難題慢慢拆開的樂趣。",
    minAge: 8,
    maxAge: 12,
    weight: 4,
    effects: { intellect: 6, stamina: -1 },
    tags: ["school", "growth"],
  },
  {
    id: "school-bullying",
    title: "不太像玩笑的玩笑",
    text: "同學拿你的某個特點反覆開玩笑，你開始分辨一起笑與被迫陪笑的差別。",
    minAge: 8,
    maxAge: 12,
    weight: 3,
    effects: { health: -4, empathy: 4, charm: -2 },
    tags: ["school", "crisis"],
  },
  {
    id: "school-field-trip",
    title: "沒有家人陪同的校外教學",
    text: "你自己保管零用錢、跟同學分組，也第一次覺得世界比住家附近大很多。",
    minAge: 7,
    maxAge: 12,
    weight: 5,
    effects: { luck: 3, charm: 2, money: -25 },
    tags: ["school", "growth"],
  },
  {
    id: "school-allowance",
    title: "第一次管理零用錢",
    text: "想買的東西比口袋裡的錢多，你開始學會等待、比較和後悔。",
    minAge: 8,
    maxAge: 12,
    weight: 5,
    effects: { intellect: 3, money: 15 },
    tags: ["school", "money"],
  },
  {
    id: "school-recital",
    title: "台上那一小段獨奏",
    text: "手心都是汗，你仍把練習幾個月的旋律完整彈完。",
    minAge: 7,
    maxAge: 12,
    weight: 4,
    effects: { charm: 5, stamina: -1 },
    tags: ["school", "creative"],
  },
  {
    id: "school-sports-day",
    title: "運動會最後一棒",
    text: "你接過接力棒時全班都在喊，名次忽然沒有想像中那麼重要。",
    minAge: 7,
    maxAge: 12,
    weight: 5,
    effects: { stamina: 5, health: 2, charm: 2 },
    tags: ["school", "health"],
  },
  {
    id: "school-teacher-praise",
    title: "被老師看見的進步",
    text: "老師沒有稱讚分數，而是說你比上次更願意把不懂的地方問清楚。",
    minAge: 7,
    maxAge: 12,
    weight: 5,
    effects: { intellect: 4, empathy: 2 },
    tags: ["school", "growth"],
  },
  {
    id: "teen-exam-fail",
    title: "努力後仍不理想的成績",
    text: "你看著紅字很久，第一次必須決定失敗是結論，還是下一次調整的方法。",
    minAge: 13,
    maxAge: 17,
    weight: 5,
    effects: { health: -3, intellect: 4 },
    tags: ["teen", "school"],
  },
  {
    id: "teen-online-rumor",
    title: "班群裡失控的傳言",
    text: "一張被截掉前後文的圖片快速流傳，你第一次感到網路上的字也能真正傷人。",
    minAge: 13,
    maxAge: 17,
    weight: 3,
    effects: { health: -5, empathy: 4, charm: -2 },
    tags: ["teen", "social", "crisis"],
  },
  {
    id: "teen-club-stage",
    title: "社團第一次正式上場",
    text: "練習時的錯誤都在正式開始後安靜下來，你只剩下眼前要完成的事。",
    minAge: 13,
    maxAge: 17,
    weight: 5,
    effects: { charm: 5, stamina: 2 },
    tags: ["teen", "school"],
  },
  {
    id: "teen-menstruation",
    title: "生理期打亂重要的一天",
    text: "腹痛與疲倦讓原本的安排變得困難，你開始更仔細理解自己的身體週期。",
    minAge: 13,
    maxAge: 17,
    weight: 3,
    genders: ["female"],
    effects: { health: -3, intellect: 2 },
    tags: ["teen", "health", "gender"],
  },
  {
    id: "teen-voice-change",
    title: "聲音忽然不受控制",
    text: "回答問題時聲音突然破掉，全班笑了一下，你只想立刻消失在座位裡。",
    minAge: 13,
    maxAge: 16,
    weight: 3,
    genders: ["male"],
    effects: { charm: -2, empathy: 2, health: 1 },
    tags: ["teen", "health", "gender"],
  },
  {
    id: "teen-part-time",
    title: "週末的第一份打工",
    text: "站了一整天之後，你才明白薪水不只是一個數字，也是被換走的時間和體力。",
    minAge: 16,
    maxAge: 17,
    weight: 4,
    effects: { money: 55, stamina: -3, empathy: 2 },
    tags: ["teen", "work"],
  },
  {
    id: "teen-curfew",
    title: "晚歸後的家庭衝突",
    text: "你覺得自己已經長大，家人卻只看見沒有回覆的訊息與越來越晚的時間。",
    minAge: 14,
    maxAge: 17,
    weight: 4,
    effects: { empathy: 3, health: -2, charm: -1 },
    tags: ["teen", "family"],
  },
  {
    id: "teen-friendship-break",
    title: "最好的朋友開始疏遠",
    text: "你們沒有真正吵架，只是不再第一個告訴彼此新發生的事。",
    minAge: 13,
    maxAge: 17,
    weight: 4,
    effects: { health: -3, empathy: 5 },
    tags: ["teen", "friendship"],
  },
  {
    id: "teen-competition",
    title: "只差一點的比賽結果",
    text: "你離名次只差很小的距離，那份不甘心讓接下來的練習變得更有方向。",
    minAge: 13,
    maxAge: 17,
    weight: 4,
    effects: { stamina: 3, intellect: 3, luck: -1 },
    tags: ["teen", "growth"],
  },
  {
    id: "teen-volunteer",
    title: "第一次長時間志工服務",
    text: "真正需要幫忙的人和課本裡的案例不太一樣，你學會先聽再行動。",
    minAge: 14,
    maxAge: 17,
    weight: 4,
    effects: { empathy: 6, stamina: -1, charm: 2 },
    tags: ["teen", "community"],
  },
  {
    id: "teen-future-form",
    title: "升學志願表上的空格",
    text: "大人一直問未來要做什麼，你第一次承認自己還不知道，也開始認真查找答案。",
    minAge: 15,
    maxAge: 17,
    weight: 5,
    effects: { intellect: 5, health: -2 },
    tags: ["teen", "school"],
  },
  {
    id: "young-dorm-roommate",
    title: "第一次與陌生人同住",
    text: "作息、整潔與冷氣溫度都需要協商，你發現獨立生活有一半是在學會溝通。",
    minAge: 18,
    maxAge: 24,
    weight: 5,
    effects: { empathy: 4, stamina: 2, health: -1 },
    tags: ["young", "life"],
  },
  {
    id: "young-tuition-pressure",
    title: "學費與生活費同時到期",
    text: "帳單讓你第一次認真計算一個學期的每一筆支出，也重新衡量打工時間。",
    minAge: 18,
    maxAge: 24,
    weight: 5,
    effects: { money: -100, intellect: 3, stamina: -2 },
    tags: ["young", "school", "money"],
  },
  {
    id: "young-first-paycheck",
    title: "第一筆正式薪水",
    text: "數字不算大，你仍把入帳畫面看了好幾次，開始想像自己能負擔的生活。",
    minAge: 18,
    maxAge: 24,
    weight: 5,
    effects: { money: 120, charm: 2, stamina: -2 },
    tags: ["young", "career"],
  },
  {
    id: "young-internship",
    title: "實習裡沒有人教的規則",
    text: "你學會會議中何時發言、如何問問題，也看見理想職業真正日常的一面。",
    minAge: 19,
    maxAge: 24,
    weight: 5,
    effects: { intellect: 5, charm: 3, stamina: -3 },
    tags: ["young", "career"],
  },
  {
    id: "young-conscription",
    title: "暫時離開原本生活的役期",
    text: "高度規律的集體生活打亂原本計畫，也逼你重新理解體力、服從與同伴關係。",
    minAge: 18,
    maxAge: 24,
    weight: 3,
    genders: ["male"],
    effects: { stamina: 5, empathy: 2, money: -30, health: -1 },
    tags: ["young", "gender", "life"],
  },
  {
    id: "young-workplace-bias",
    title: "面試裡不該被問的問題",
    text: "對方繞著婚育與家庭計畫提問，你清楚感到能力之外的標準正在影響機會。",
    minAge: 20,
    maxAge: 29,
    weight: 3,
    genders: ["female"],
    effects: { charm: -2, intellect: 4, health: -2 },
    tags: ["young", "gender", "career"],
  },
  {
    id: "adult-daycare-waitlist",
    title: "托育候補名單很長",
    text: "孩子的照顧安排牽動兩個人的工作，你們只好把時間、接送與費用重新排過一遍。",
    minAge: 20,
    maxAge: 39,
    weight: 4,
    requiresChildren: true,
    effects: { money: -90, empathy: 4, health: -2 },
    tags: ["adult", "parenting"],
  },
  {
    id: "adult-parent-work-conflict",
    title: "孩子生病與重要會議撞期",
    text: "電話響起時你已經坐進會議室，家庭和工作同時要求你立刻做出選擇。",
    minAge: 22,
    maxAge: 45,
    weight: 5,
    requiresChildren: true,
    effects: { money: -55, empathy: 5, health: -3 },
    tags: ["adult", "parenting", "career"],
  },
  {
    id: "adult-school-supplies",
    title: "新學期的採買清單",
    text: "制服、鞋子、教材與活動費一項項加起來，你開始理解教育支出沒有真正的淡季。",
    minAge: 25,
    maxAge: 49,
    weight: 5,
    requiresChildren: true,
    effects: { money: -110, intellect: 1, empathy: 2 },
    tags: ["adult", "parenting", "money"],
  },
  {
    id: "adult-partner-night-talk",
    title: "孩子睡後的財務會議",
    text: "你和伴侶壓低聲音討論帳單、工作與明年的安排，親密有時就是一起面對不漂亮的數字。",
    minAge: 22,
    maxAge: 45,
    weight: 4,
    requiresPartner: true,
    requiresChildren: true,
    effects: { intellect: 3, empathy: 4, health: -1 },
    tags: ["adult", "family"],
  },
  {
    id: "adult-fertility-check",
    title: "一次讓人緊張的婦科檢查",
    text: "檢查結果沒有立即的答案，醫師仔細說明年齡、健康與生育並不是簡單的是非題。",
    minAge: 25,
    maxAge: 45,
    weight: 3,
    genders: ["female"],
    effects: { health: 3, money: -70, empathy: 2 },
    tags: ["adult", "gender", "health"],
  },
  {
    id: "adult-father-leave",
    title: "是否主動請育兒假",
    text: "職場暗示你最好不要離開太久，家裡卻正需要有人真正承擔白天的照顧。",
    minAge: 20,
    maxAge: 45,
    weight: 3,
    genders: ["male"],
    requiresChildren: true,
    effects: { money: -80, empathy: 7, charm: 2 },
    tags: ["adult", "gender", "parenting"],
  },
  {
    id: "middle-menopause",
    title: "身體進入新的週期",
    text: "睡眠與體溫變得不穩定，你開始調整生活，也更坦然地面對身體的長期變化。",
    minAge: 45,
    maxAge: 55,
    weight: 4,
    genders: ["female"],
    effects: { health: -3, intellect: 2, empathy: 2 },
    tags: ["middle", "gender", "health"],
  },
  {
    id: "middle-health-screening",
    title: "健檢報告出現警示數字",
    text: "醫師說還來得及調整。你第一次把運動和飲食當成必須，而不是有空再做。",
    minAge: 40,
    maxAge: 59,
    weight: 5,
    effects: { health: 4, money: -80, stamina: 2 },
    tags: ["middle", "health"],
  },
  {
    id: "middle-child-tuition",
    title: "孩子提出一筆重要的進修費",
    text: "你看得見這筆錢可能換來的機會，也看得見家庭帳戶會因此變得很薄。",
    minAge: 40,
    maxAge: 59,
    weight: 4,
    requiresChildren: true,
    effects: { money: -180, empathy: 5, intellect: 1 },
    tags: ["middle", "parenting", "money"],
  },
  {
    id: "middle-parent-memory",
    title: "長輩開始忘記熟悉的路",
    text: "同一件事發生第二次後，你知道不能再只說是年紀大了，照顧安排也因此改變。",
    minAge: 40,
    maxAge: 64,
    weight: 4,
    effects: { empathy: 7, money: -100, health: -3 },
    tags: ["middle", "family", "health"],
  },
  {
    id: "elder-retirement-class",
    title: "退休後第一次去上課",
    text: "教室裡每個人都帶著不同人生前來，你重新習慣當一個可以犯錯的初學者。",
    minAge: 60,
    maxAge: 88,
    weight: 5,
    effects: { intellect: 5, charm: 2, money: -35 },
    tags: ["elder", "growth"],
  },
  {
    id: "elder-fall-prevention",
    title: "家裡開始加裝扶手",
    text: "承認身體需要協助並不容易，但一次安全改造換來更多能自己生活的日子。",
    minAge: 65,
    maxAge: 88,
    weight: 5,
    effects: { health: 6, money: -75, stamina: 1 },
    tags: ["elder", "health", "asset"],
  },
  {
    id: "elder-old-friend",
    title: "老朋友的告別式",
    text: "照片裡的人一直停在年輕時，你回家後整理了很久沒有打開的聯絡簿。",
    minAge: 65,
    maxAge: 88,
    weight: 3,
    effects: { health: -3, empathy: 6 },
    tags: ["elder", "friendship"],
  },
  {
    id: "elder-pension-check",
    title: "重新計算退休金能用多久",
    text: "醫療、物價與住房讓原本的估算變得不夠，你開始縮減支出，也重新安排資產。",
    minAge: 60,
    maxAge: 88,
    weight: 5,
    effects: { intellect: 4, money: 70, health: -1 },
    tags: ["elder", "money"],
  },
  {
    id: "student-parent-exam-night",
    title: "考試週與孩子的夜哭",
    text: "明早要考試，孩子卻整夜睡不安穩。你抱著課本來回哄睡，第一次真正感到兩種責任同時壓在身上。",
    minAge: 18,
    maxAge: 24,
    weight: 5,
    requiresChildren: true,
    effects: { intellect: 2, stamina: -5, health: -3, empathy: 5 },
    tags: ["young", "school", "parenting"],
  },
  {
    id: "student-parent-campus-care",
    title: "校園裡找不到合適托育",
    text: "課表與托育時間總差一小段，你只好在同學、伴侶與家人之間拼出能上課的空隙。",
    minAge: 18,
    maxAge: 24,
    weight: 4,
    requiresChildren: true,
    effects: { money: -75, intellect: 2, empathy: 4, stamina: -3 },
    tags: ["young", "school", "parenting"],
  },
  {
    id: "student-parent-judgment",
    title: "同齡人不經意的評論",
    text: "有人把太早成為父母說得像一句玩笑，你沒有回嘴，只更清楚自己每天正在承擔什麼。",
    minAge: 18,
    maxAge: 24,
    weight: 3,
    requiresChildren: true,
    effects: { health: -3, empathy: 4, charm: -1 },
    tags: ["young", "social", "parenting"],
  },
  {
    id: "student-parent-budget",
    title: "學費與尿布出現在同一張帳單",
    text: "你刪掉幾項自己的支出，仍必須思考要多打一份工，還是延後部分課程。",
    minAge: 18,
    maxAge: 24,
    weight: 5,
    requiresChildren: true,
    effects: { money: -120, intellect: 3, stamina: -4 },
    tags: ["young", "money", "parenting"],
  },
];

type EventSeed = [
  title: string,
  text: string,
  effects: Partial<Stats>,
  relation?: Pick<GameEvent, "targetRelation" | "targetMinAge" | "targetMaxAge">,
];

const EXPANDED_EVENT_GROUPS: Array<{
  id: string;
  minAge: number;
  maxAge: number;
  tags: string[];
  events: EventSeed[];
}> = [
  {
    id: "early-childhood",
    minAge: 0,
    maxAge: 5,
    tags: ["childhood", "family"],
    events: [
      ["第一次自己穿好鞋", "左右腳穿反了，你仍堅持自己走到門口；家人忍住笑，讓你把小小的自主完成。", { stamina: 2, intellect: 1 }],
      ["找不到的安撫巾", "睡前怎麼也找不到熟悉的安撫巾，哭累後你在家人的陪伴裡慢慢學會用別的方法入睡。", { health: -1, empathy: 2 }],
      ["積木塔倒下來", "你把積木疊得比頭還高，倒下時嚇了一跳，隔幾分鐘又從第一塊重新開始。", { intellect: 3, stamina: 1 }],
      ["第一次離開照顧者半天", "幼兒園門口的分離比想像中難，下午再見到家人時，你已能說出今天認識的一位朋友。", { charm: 2, empathy: 2, health: -1 }],
      ["不肯吃的青菜", "餐桌上的青菜引發一場漫長拉鋸，最後你願意試一小口，也讓全家晚餐晚了半小時。", { health: 1, stamina: -1 }],
      ["雨天踩進水窪", "新鞋濕透，回家後還有點著涼；那個濺起很高的水花卻讓你笑了一整路。", { luck: 2, health: -2, money: -12 }],
      ["牆上的第一幅畫", "你用蠟筆把想像中的家畫在牆上，得到一張真正的畫紙，也一起把牆擦乾淨。", { charm: 3, empathy: 1, money: -8 }],
      ["半夜的成長痛", "雙腿酸痛讓你睡不好，家人輕輕按摩到你重新睡著，隔天大家都帶著黑眼圈。", { health: -2, empathy: 2 }],
      ["叫出鄰居的名字", "你在電梯裡準確叫出每天見面的鄰居，意外替原本陌生的大人打開一段日常交談。", { charm: 3, empathy: 2 }],
      ["玩具交換的後悔", "你答應和朋友交換玩具，回家後又捨不得；家人陪你學著把承諾與感受一起說清楚。", { empathy: 3, charm: -1, intellect: 1 }],
    ],
  },
  {
    id: "primary-school",
    minAge: 6,
    maxAge: 11,
    tags: ["childhood", "school"],
    events: [
      ["忘在抽屜的聯絡簿", "直到睡前才想起聯絡簿留在學校，你第一次承擔忘東西造成的不方便。", { intellect: 1, health: -1, empathy: 1 }],
      ["小組報告沒有人開口", "全組都不敢上台，你握著皺掉的稿子先說第一句，聲音很小但沒有停下。", { charm: 4, stamina: -1 }],
      ["校外教學的零用錢", "你把零用錢花在紀念品上，回程才發現沒有錢買點心，第一次明白預算有先後順序。", { intellect: 3, money: -15 }],
      ["操場上的擦傷", "追球時跌倒擦破膝蓋，你休息幾天，也學會在逞強前先看身體狀況。", { stamina: 2, health: -3 }],
      ["被選為值日生長", "你要提醒同學整理教室，既怕被討厭又不能假裝沒看見，最後找到比較不刺人的說法。", { empathy: 3, charm: 2 }],
      ["第一次段考退步", "成績比預期低，家裡沒有立刻責備，而是一起找出你根本沒讀懂的章節。", { intellect: 4, health: -2 }],
      ["同學忘了帶午餐", "你分出一半便當，下午餓得比平常快，卻多了一位願意和你並肩回家的朋友。", { empathy: 5, stamina: -2 }],
      ["才藝課想放棄", "反覆練習仍做不好讓你很挫折，你決定先停一學期，而不是把每次退出都當成失敗。", { health: 2, charm: -1, intellect: 2, money: 20 }],
      ["在圖書館弄丟借書", "一本借來的書不見了，你用零用錢賠償並花一週重走每天的路線。", { intellect: 2, money: -25, luck: -1 }],
      ["班級市集的找零錯誤", "忙亂中少找了一筆錢，全班一起盤點後補上差額，你也開始對數字多看一遍。", { intellect: 4, money: -10, empathy: 1 }],
    ],
  },
  {
    id: "teen-years",
    minAge: 12,
    maxAge: 17,
    tags: ["teen", "school"],
    events: [
      ["社群貼文被誤解", "一句本來只想逗朋友笑的話被截圖轉傳，你花了幾天道歉，也重新理解公開文字的重量。", { charm: -3, empathy: 5, health: -1 }],
      ["第一次模擬考失常", "熟悉的題目在考場上突然一片空白，你開始練習睡眠與壓力管理，而不只是把課表塞滿。", { intellect: 3, health: -3, stamina: 2 }],
      ["社團成果展臨時缺人", "主力同學生病，你被推上不熟的位置；表演不完美，團隊卻真的撐到了謝幕。", { charm: 5, stamina: -3, empathy: 2 }],
      ["打工薪水的第一筆支出", "你用第一筆打工薪水買了期待很久的東西，月底才發現交通與餐費仍要自己負擔。", { money: 35, intellect: 2, stamina: -2 }],
      ["朋友把祕密說出去", "信任突然出現裂縫，你沒有立刻絕交，而是決定哪些界線必須重新說清楚。", { empathy: 3, charm: -2, health: -2 }],
      ["選組方向和家人不同", "家人擔心你的選擇太冒險，你準備資料說明，最後得到有條件的支持。", { intellect: 5, empathy: 1, health: -2 }],
      ["熬夜追進度", "報告準時交出，連續幾晚的睡眠不足卻讓你在白天幾乎無法專心。", { intellect: 4, health: -5, stamina: -3 }],
      ["校隊受傷後坐板凳", "腳踝受傷讓你錯過重要比賽，你從場邊記錄戰術，也第一次看見不上場的隊員如何影響全隊。", { health: -5, intellect: 3, empathy: 3 }],
      ["匿名帳號的惡意留言", "陌生人的評論讓你懷疑自己，關掉通知後，你找可信任的人確認哪些意見值得留下。", { health: -3, charm: -1, intellect: 2 }],
      ["二手教材交換", "你整理不再使用的參考書，和學弟妹交換需要的教材，省下一筆錢也認識新的讀書夥伴。", { money: 45, empathy: 3, intellect: 2 }],
    ],
  },
  {
    id: "young-adult",
    minAge: 18,
    maxAge: 24,
    tags: ["young", "school", "work"],
    events: [
      ["租屋押金比預期更高", "喜歡的房間需要兩個月押金，你只好放棄部分家具，先把能睡與能工作的角落安頓好。", { money: -120, intellect: 2, health: -1 }],
      ["選錯一門必修課", "直到期中才發現選課條件不符，退選會延後進度，硬撐則要犧牲打工時間。", { intellect: 3, money: -45, stamina: -3 }],
      ["實習內容和承諾不同", "原本說好的學習機會變成大量雜務，你蒐集工作紀錄後和主管談清楚界線。", { charm: 3, intellect: 3, health: -2 }],
      ["朋友邀你共同租屋", "房租能省下不少，但生活習慣和金錢規則必須在搬進去前逐條談妥。", { money: 70, empathy: 3, charm: -1 }],
      ["第一張信用卡帳單", "分期讓支出看起來很輕，總額卻沒有消失；你取消幾個訂閱並開始記帳。", { money: -70, intellect: 4 }],
      ["凌晨急診陪朋友", "你放下隔天的課與工作，在急診室陪朋友等到天亮，代價是錯過一次重要報告。", { empathy: 6, intellect: -2, stamina: -4 }],
      ["比賽獎金意外入帳", "曾經差點放棄的作品得到小型比賽獎金，你拿一部分改善設備，剩下的存進緊急預備金。", { money: 130, charm: 3, luck: 3 }],
      ["求職面試連續落選", "幾次面試都沒有下文，你把挫折拆成履歷、作品與表達三個能修正的部分。", { health: -3, intellect: 4, charm: 2 }],
      ["通勤車壞在雨夜", "維修費吞掉半個月生活預算，你學會保留交通備案，也不得不取消原本的旅行。", { money: -95, health: -1, intellect: 2 }],
      ["接到超出能力的案子", "報酬很好，時程卻幾乎不可能；你協商縮小範圍，少賺一點但保住睡眠和信用。", { money: 85, intellect: 4, health: -2 }],
    ],
  },
  {
    id: "building-life",
    minAge: 25,
    maxAge: 34,
    tags: ["adult", "money", "work"],
    events: [
      ["加薪伴隨更長工時", "薪資調整終於到來，新的責任也讓晚餐時間一再往後；你得決定這種交換能維持多久。", { money: 170, stamina: -4, health: -3 }],
      ["租約突然不續租", "房東決定收回房子，你在一個月內找房、打包與搬家，存款也少了一截。", { money: -160, stamina: -4, luck: -1 }],
      ["朋友提出合夥邀請", "企畫有吸引力，現金流卻還不清楚；你只投入能承受損失的金額並要求白紙黑字。", { money: -90, intellect: 5, luck: 3 }],
      ["健康檢查亮起警示", "報告上的幾個紅字還不到疾病，卻足以提醒你長期熬夜不是免費的。", { health: -4, intellect: 2, stamina: 2 }],
      ["旅程遇上航班取消", "期待很久的假期被迫縮短，退費流程耗掉許多時間，臨時改道卻看見原本不會去的城市。", { money: -65, luck: 3, stamina: -2 }],
      ["報稅時發現漏報", "一筆兼職收入沒有正確申報，你補繳費用，也把之後的收據與合約重新分類。", { money: -75, intellect: 4 }],
      ["舊同學帶來工作機會", "職位更有發展，但試用期薪資較低；你接受短期退一步，換取新的專業軌道。", { money: -40, intellect: 6, luck: 3 }],
      ["家中電器同月故障", "冰箱和洗衣機幾乎同時罷工，你比較維修、二手與新品價格後才做決定。", { money: -115, intellect: 2, stamina: -1 }],
      ["網購投資課程踩雷", "華麗廣告承諾快速翻倍，內容卻只有空泛口號；你申請退款並提醒朋友別再跟進。", { money: -60, intellect: 3, empathy: 2 }],
      ["週末真正休息一次", "你拒絕臨時工作，把手機放遠，睡飽後才發現疲勞已經累積很久。", { health: 5, stamina: 4, money: -30 }],
    ],
  },
  {
    id: "family-career",
    minAge: 35,
    maxAge: 44,
    tags: ["adult", "family", "career"],
    events: [
      ["團隊重組後的位置變了", "熟悉的主管離開，你的工作被拆成兩半；新機會和失落感同時出現。", { intellect: 4, charm: 2, health: -3 }],
      ["房貸利率重新調整", "每月支出突然增加，你重新談條件、削減娛樂，也暫停一筆風險投資。", { money: -140, intellect: 5, health: -1 }],
      ["父母第一次需要陪診", "{relative} 原本只是做一次檢查，最後占去整天；你開始把長輩照護排進工作的長期計畫。", { empathy: 6, stamina: -4, money: -45 }, { targetRelation: "parent", targetMinAge: 50 }],
      ["升遷機會需要外派", "職稱和收入都很吸引人，代價是離開熟悉的家庭支持網。", { money: 210, charm: 3, empathy: -3, health: -2 }],
      ["孩子的補習費超出預算", "{relative} 的成績焦慮讓支出不斷加碼，你停下比較，改和孩子一起確認真正需要的協助。", { money: -90, empathy: 4, intellect: 2 }, { targetRelation: "descendant", targetMinAge: 6, targetMaxAge: 22 }],
      ["多年朋友借一筆大錢", "你願意幫忙，但只借出不會傷害家庭生活的金額，友情因此多了一點尷尬。", { money: -80, empathy: 4, charm: -2 }],
      ["腰痛讓你被迫停工", "原本以為忍幾天就好，醫師要求復健與調整工作姿勢，收入和節奏都被打亂。", { health: -6, money: -75, intellect: 2 }],
      ["副業終於穩定接單", "幾年的零碎累積開始帶來固定客戶，但週末也不再完全屬於自己。", { money: 180, charm: 4, stamina: -4 }],
      ["社區改建帶來噪音", "工程讓居住品質下降，協調會議很漫長；你和鄰居一起爭取到合理施工時段。", { health: -2, charm: 3, empathy: 3 }],
      ["家庭旅行有人臨時生病", "取消行程會損失訂金，勉強出發又不安全；你選擇留下照顧，假期變成安靜休養。", { money: -70, empathy: 5, health: 2 }],
    ],
  },
  {
    id: "middle-years",
    minAge: 45,
    maxAge: 54,
    tags: ["middle", "health", "money"],
    events: [
      ["公司提供優退方案", "一次領取的金額很可觀，之後的收入卻不確定；你花幾週把最壞情況也算進去。", { money: 220, intellect: 5, health: -3 }],
      ["視力變化影響工作", "螢幕上的小字開始模糊，你配了新眼鏡，也承認身體正在要求不同的使用方式。", { money: -55, health: 2, intellect: 1 }],
      ["老屋漏水擴大", "一小塊水痕變成牆內管線問題，修繕費比預期高，拖延只會更昂貴。", { money: -180, health: -1 }],
      ["多年保單重新檢視", "你發現重複保障與缺口並存，取消不合適的項目後，終於看懂每月支出去向。", { intellect: 5, money: 85 }],
      ["孩子第一次反過來請客", "{relative} 請的那頓飯不昂貴，卻讓你感到照顧的方向正在慢慢改變。", { empathy: 6, charm: 2, money: 20 }, { targetRelation: "descendant", targetMinAge: 18 }],
      ["同事突然重病", "熟悉的人暫停工作治療，你接手部分責任，也重新安排自己的健康檢查。", { empathy: 4, stamina: -4, health: 2 }],
      ["投資帳面大幅下跌", "市場消息讓人想立刻賣出，你回頭確認當初的風險承受範圍，而不是只看今天的紅字。", { money: -160, intellect: 4, health: -2 }],
      ["轉職後要向年輕人學習", "你不再是團隊裡最熟練的人，放下身段提問反而讓新專業長得更快。", { intellect: 6, charm: 2, empathy: 2 }],
      ["照護與工作撞期", "{relative} 回診和重要會議在同一天，你請家人分工，仍付出加班補進度的代價。", { empathy: 5, stamina: -5, money: -40 }, { targetRelation: "parent", targetMinAge: 55 }],
      ["開始固定重量訓練", "前幾週全身痠痛，持續後你發現搬東西與上下樓都不再那麼吃力。", { stamina: 6, health: 5, money: -45 }],
    ],
  },
  {
    id: "pre-retirement",
    minAge: 55,
    maxAge: 64,
    tags: ["middle", "retirement", "health"],
    events: [
      ["退休試算少算了通膨", "原本看似足夠的數字縮水，你延後一項大額支出並增加現金緩衝。", { intellect: 5, money: 80, health: -1 }],
      ["膝蓋不再接受逞強", "一次長途健行後膝蓋腫痛，你改用循序訓練，也放棄證明自己還和年輕時一樣。", { health: -4, empathy: 2, intellect: 2 }],
      ["老同事一起接顧問案", "經驗真的能換成收入，但客戶仍期待即時回覆，你重新設定工作邊界。", { money: 150, intellect: 4, stamina: -3 }],
      ["父母家需要無障礙改造", "{relative} 住處的浴室與樓梯安全不能再拖，你分擔費用，也參與每一項施工決定。", { money: -160, empathy: 6, health: -1 }, { targetRelation: "parent", targetMinAge: 60 }],
      ["孩子搬回家暫住", "{relative} 的工作遇到轉折，家裡重新變擁擠；你提供緩衝，也說清楚共同生活規則。", { empathy: 5, money: -55, charm: -1 }, { targetRelation: "descendant", targetMinAge: 18 }],
      ["睡眠檢查找到原因", "長期疲倦終於不是意志力問題，治療需要花錢，也讓白天精神逐漸恢復。", { health: 7, money: -90, stamina: 3 }],
      ["被邀請擔任社區志工", "你把多年專業用在社區，成就感增加，私人時間也少了一部分。", { empathy: 6, charm: 4, stamina: -3 }],
      ["定存到期重新配置", "高報酬商品很誘人，你只把小部分資金放進風險資產，保留醫療與生活現金。", { money: 100, intellect: 5, luck: -1 }],
      ["牙齒需要一連串治療", "拖了多年的問題一次爆發，療程昂貴又耗時，處理後吃飯終於不再小心翼翼。", { money: -130, health: 5, stamina: -2 }],
      ["第一次練習完全不工作的一週", "沒有行事曆填滿的日子反而讓人不安，你慢慢找到不靠職稱也能成立的生活節奏。", { health: 5, empathy: 3, money: -35 }],
    ],
  },
  {
    id: "retired-life",
    minAge: 65,
    maxAge: 74,
    tags: ["elder", "retirement", "family"],
    events: [
      ["退休後第一個平常日", "不用趕著出門的早晨很自由，也有點空；你替自己安排散步、採買與固定見朋友的時間。", { health: 4, empathy: 2, charm: 1 }],
      ["醫療自費項目超出預期", "治療選項各有代價，你詢問第二意見後選擇能負擔且真正需要的方案。", { money: -140, health: 4, intellect: 3 }],
      ["晚輩請你教一項老技能", "{relative} 想學你以為早就沒人需要的手藝，突然成為兩個世代一起度過下午的理由。", { empathy: 7, intellect: 2, stamina: -1 }, { targetRelation: "descendant", targetMinAge: 3 }],
      ["旅遊保險拒賠爭議", "一場延誤衍生繁瑣申訴，你整理證明文件，最後拿回部分損失。", { money: 45, intellect: 4, health: -2 }],
      ["老朋友開始固定共餐", "每週一次的午餐讓彼此更早發現健康和情緒變化，也多了一筆固定花費。", { empathy: 5, health: 3, money: -30 }],
      ["差點點進詐騙連結", "訊息看起來像家人求助，你在轉帳前打電話確認，驚險保住存款。", { intellect: 5, health: -1, luck: 2 }],
      ["家中雜物需要斷捨離", "每件物品都有故事，真正整理起來比搬家還累；清出的空間讓生活安全許多。", { health: 3, stamina: -3, empathy: 2 }],
      ["退休金投資出現波動", "不再有固定薪水後，每一次下跌都更刺眼，你把生活費和長期資產徹底分開。", { money: -90, intellect: 4, health: -1 }],
      ["自費參加一門新課", "班上大多是陌生人，你從最慢的進度開始，重新體會不熟練的樂趣。", { intellect: 5, charm: 3, money: -45 }],
      ["幫忙照顧年幼晚輩過度疲勞", "你很想替 {relative} 分擔，身體卻提醒你不能把全部責任都接下來。", { empathy: 5, stamina: -5, health: -3 }, { targetRelation: "descendant", targetMaxAge: 17 }],
    ],
  },
  {
    id: "late-life",
    minAge: 75,
    maxAge: 88,
    tags: ["elder", "legacy", "health"],
    events: [
      ["藥盒裡的藥變多了", "不同科別的用藥彼此影響，你請藥師重新整理，減少重複也避免漏吃。", { health: 4, intellect: 2, money: -35 }],
      ["不再適合獨自開車", "放下駕駛座意味著失去一部分自由，你和家人重畫交通方式，也保住更多安全。", { health: 5, charm: -1, money: -25 }],
      ["老照片的名字想不起來", "有一張合照怎麼也想不起其中一人的名字，你把還記得的故事錄音留給家人。", { intellect: -3, empathy: 6 }],
      ["浴室裡的一次滑倒", "沒有骨折已經很幸運，之後的扶手、防滑與照明改造不能再等。", { health: -7, money: -90, luck: 2 }],
      ["決定整理重要文件", "保單、帳戶、醫療意願與聯絡方式終於放進同一個資料夾，家人也知道該去哪裡找。", { intellect: 5, empathy: 5, health: -1 }],
      ["社區鄰居輪流問候", "你一整天沒拉開窗簾，鄰居便來敲門；平日的小交情成為晚年可靠的安全網。", { empathy: 6, charm: 3, health: 2 }],
      ["聽力輔具需要適應", "聲音重新變清楚，也帶來一段刺耳的適應期；幾週後你不再靠猜測參與談話。", { money: -80, charm: 3, health: 3 }],
      ["把珍藏送給真正需要的人", "你沒有等到物品被遺忘，親手說明來歷，再交給願意繼續使用的家人。", { empathy: 7, money: -20, charm: 2 }],
      ["住院後重新安排生活", "出院不代表完全恢復，你接受短期照護並刪掉不必要的責任，讓身體有時間站穩。", { health: -4, money: -120, empathy: 4 }],
      ["生日聚會改成白天", "晚上的體力不再允許長時間聚會，家人把餐會改到中午，反而留下更清醒的合照。", { health: 3, empathy: 6, stamina: -1 }],
    ],
  },
];

const EXPANDED_RANDOM_EVENTS: GameEvent[] = EXPANDED_EVENT_GROUPS.flatMap((group) =>
  group.events.map(([title, text, effects, relation], index) => ({
    id: `v2-${group.id}-${index + 1}`,
    title,
    text,
    minAge: group.minAge,
    maxAge: group.maxAge,
    weight: 4,
    effects,
    tags: group.tags,
    ...relation,
  })),
);

export const RANDOM_EVENTS: GameEvent[] = [...CORE_RANDOM_EVENTS, ...EXPANDED_RANDOM_EVENTS];

export const CHILD_RANDOM_EVENTS: ChildRandomEvent[] = [
  { id: "child-nightmare", title: "半夜做了惡夢", text: "{child} 半夜驚醒，隔天精神不濟；一段安靜陪伴讓孩子慢慢說出害怕的事。", minAge: 3, maxAge: 8, weight: 5, effects: { health: -2, empathy: 3 } },
  { id: "child-first-friend", title: "第一次主動交朋友", text: "{child} 主動邀一位總是獨處的同學一起玩，兩個孩子從有點尷尬的沉默開始熟悉。", minAge: 4, maxAge: 10, weight: 6, effects: { charm: 3, empathy: 4 } },
  { id: "child-lost-lunchbox", title: "弄丟了便當袋", text: "{child} 找遍教室仍沒找到便當袋，只好用零用錢補買，也開始練習自己整理物品。", minAge: 6, maxAge: 11, weight: 5, effects: { intellect: 2, money: -8 } },
  { id: "child-reading", title: "迷上一套長篇故事", text: "{child} 每天追著讀下一章，睡前時間變長，早上也偶爾爬不起來。", minAge: 7, maxAge: 13, weight: 5, effects: { intellect: 5, health: -1 } },
  { id: "child-team-loss", title: "比賽輸在最後一刻", text: "{child} 的隊伍在最後一刻輸掉比賽，難過幾天後才願意回去和隊友一起檢討。", minAge: 8, maxAge: 16, weight: 5, effects: { stamina: 3, empathy: 3, health: -1 } },
  { id: "child-bullying-witness", title: "看見同學被排擠", text: "{child} 猶豫很久後向老師說明情況，也擔心自己成為下一個被排擠的人。", minAge: 9, maxAge: 15, weight: 4, effects: { empathy: 6, charm: -1, health: -2 } },
  { id: "child-exam-improve", title: "找到適合自己的讀書方法", text: "{child} 不再只延長讀書時間，改用整理與練習檢查盲點，成績終於穩定進步。", minAge: 10, maxAge: 18, weight: 5, effects: { intellect: 6, stamina: -1 } },
  { id: "child-phone-repair", title: "手機摔破螢幕", text: "{child} 必須負擔部分維修費，也第一次認真比較保護、維修與換新的成本。", minAge: 12, maxAge: 20, weight: 5, effects: { money: -25, intellect: 3 } },
  { id: "child-early-love", title: "感情讓課業分心", text: "{child} 花很多時間等待訊息，成績短暫下滑，也開始摸索喜歡一個人和照顧自己之間的界線。", minAge: 14, maxAge: 19, weight: 4, effects: { charm: 3, intellect: -3, empathy: 2 } },
  { id: "child-parttime", title: "開始週末打工", text: "{child} 想自己負擔一部分生活費，收入帶來自由，休息時間卻明顯減少。", minAge: 16, maxAge: 23, weight: 5, effects: { money: 55, stamina: -3, intellect: 2 } },
  { id: "child-course-fail", title: "一門課沒有通過", text: "{child} 必須重修或改變進度，挫折也迫使孩子重新估算時間與能力。", minAge: 18, maxAge: 24, weight: 4, effects: { intellect: 3, health: -3, money: -30 } },
  { id: "child-scholarship", title: "得到一筆獎助學金", text: "{child} 的努力得到肯定，這筆資源減輕學費，也帶來維持成績的壓力。", minAge: 17, maxAge: 24, weight: 3, effects: { intellect: 5, money: 80, health: -1 } },
  { id: "child-internship", title: "實習帶來第一次職場衝突", text: "{child} 發現課本裡的正確答案到了職場不一定有人買單，開始練習協商與留下紀錄。", minAge: 19, maxAge: 26, weight: 5, effects: { intellect: 4, charm: 3, health: -2 } },
  { id: "child-move-out", title: "第一次獨立租屋", text: "{child} 搬進不大的房間，押金與家具吃掉存款，生活自主也終於有了實際重量。", minAge: 20, maxAge: 29, weight: 4, effects: { stamina: 3, money: -70, empathy: 2 } },
  { id: "child-job-rejection", title: "理想工作沒有錄取", text: "{child} 難過後重新整理作品與履歷，決定先接受一份能累積經驗的工作。", minAge: 22, maxAge: 29, weight: 5, effects: { health: -3, intellect: 4, stamina: 2 } },
  { id: "child-side-project", title: "下班後做自己的計畫", text: "{child} 用晚上累積作品，進展令人期待，睡眠與社交卻同時被壓縮。", minAge: 22, maxAge: 32, weight: 4, effects: { intellect: 4, charm: 3, health: -3 } },
  { id: "child-savings", title: "存下第一筆緊急預備金", text: "{child} 拒絕幾次衝動消費，終於存到足以應付短期失業的金額。", minAge: 21, maxAge: 32, weight: 4, effects: { money: 90, intellect: 3, charm: -1 } },
  { id: "child-burnout", title: "工作過勞的警訊", text: "{child} 連續失眠後終於請假就醫，短期收入減少，身體也得到一次喘息。", minAge: 23, maxAge: 34, weight: 4, effects: { health: -5, money: -40, empathy: 2 } },
  { id: "child-friend-wedding", title: "朋友的婚禮帶來比較", text: "{child} 開心祝福朋友，也難免回頭比較自己的進度，最後決定不把別人的時間表當期限。", minAge: 24, maxAge: 35, weight: 4, effects: { empathy: 3, health: -1, intellect: 2 } },
  { id: "child-mentor", title: "遇到願意說真話的前輩", text: "一位前輩直接指出 {child} 的盲點，也提供一次需要努力才能接住的機會。", minAge: 20, maxAge: 34, weight: 3, effects: { intellect: 5, charm: -1, luck: 3 } },
];
